import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms'
import { AffairModel } from '../../../../core/models/affair.model'
import { UserModel } from '../../../../core/models/user.model'
import { DateTools } from '../../../../core/tools/date-tools'
import { TechnicianTools } from '../../../../core/tools/technician-tools'
import {
  AvailabilityTools,
  SlotTravelInfo,
} from '../../../../core/tools/availability-tools'
import { MarkerTools } from '../../../../core/tools/marker-tools'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { DividerModule } from 'primeng/divider'
import {
  MapWithMarkersComponent,
  markerInputModel,
  RouteInputModel,
  DistanceLineInputModel,
  DepartmentBoundaryInputModel,
} from '../../../../core/components/map-with-markers/map-with-markers.component'
import { ClosestInterventionsListComponent } from '../../../../shared/components/closest-interventions-list/closest-interventions-list.component'
import { AvailabilitiesPanelComponent } from '../../../../shared/components/availabilities-panel/availabilities-panel.component'
import { DailyInfoPanelComponent } from '../../../../shared/components/daily-info-panel/daily-info-panel.component'
import { TechnicianSelectionPanelComponent } from '../../../../shared/components/technician-selection-panel/technician-selection-panel.component'
import {
  debounceTime,
  finalize,
  forkJoin,
  Observable,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs'
import { AffairsService } from '../../services/affairs.service'
import { UserService } from '../../../users/services/user.service'
import {
  InterventionsService,
  BatchInterventionResult,
  BatchSlotResult,
  ClosestWithIndicatorsResponse,
  InterventionWithIndicators,
} from '../../../interventions/services/interventions.service'
import {
  RouteCalculationService,
  InterventionIndicatorService,
  AvailabilityDataService,
  TechnicianSelectionService,
} from '../../services'
import { MessageModule } from 'primeng/message'
import { EventsService } from '../../../events/services/events.service'
import { SectorService } from '../../../sectors/services/sector.service'
import { SectorModel } from '../../../../core/models/sector.model'
import { isPointInSector } from '../../../../core/tools/sector-tools'
@Component({
  selector: 'app-affair-schedule-technicians-select',
  templateUrl: './affair-schedule-technicians-select.component.html',
  styleUrls: ['./affair-schedule-technicians-select.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ProgressSpinnerModule,
    DividerModule,
    MapWithMarkersComponent,
    MessageModule,
    ClosestInterventionsListComponent,
    AvailabilitiesPanelComponent,
    DailyInfoPanelComponent,
    TechnicianSelectionPanelComponent,
  ],
})
export class AffairScheduleTechniciansSelectComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() affair!: any
  @Input() users: any = []
  @Input() loadingUsers: boolean = false
  @Input() resetMap: boolean = false // Add reset input
  @Input() isActive: boolean = false // Add new input to detect when component becomes active
  @Input() selectedProducts: any[] = []
  @Input() initialDateRange: [Date, Date] | null = null // Add input for initial date range
  @Input() excludeInterventionId: number | null = null // ID of intervention to exclude (for reschedule)
  @Input() originalTechnicians: any[] = [] // Original technicians (for reschedule context)
  @Input() labelNewItem: 'affaire' | 'intervention' = 'affaire' // Label for the new item in daily panel
  @Input() newItemNameOverride: string | null = null // Override for new item name (e.g. mlRef for reschedule)
  @Output() techniciansSelected = new EventEmitter<UserModel[]>()
  @Output() dateRangeSelected = new EventEmitter<[Date, Date]>()
  @Output() filtersChanged = new EventEmitter<any>()
  @Output() nextStep = new EventEmitter<{
    dateRange: [Date, Date]
    technicians: UserModel[]
  }>()
  @Output() closestInterventionsChanged = new EventEmitter<any[]>() // New output for closest interventions
  @Output() techniciansByRouteChanged = new EventEmitter<
    {
      technician: UserModel
      distance_km: number
      duration_min: number | null
      distance_type: 'route' | 'haversine'
    }[]
  >()
  @Output() selectedInterventionChanged = new EventEmitter<any>() // Emit when selected intervention changes
  @Output() preSelectedSlotChanged = new EventEmitter<any>() // Emit when pre-selected slot changes
  @Output() batchIndicatorsChanged = new EventEmitter<{ [key: number]: any }>() // Emit when batch indicators change

  private affairsService = inject(AffairsService)
  private usersService = inject(UserService)
  private interventionsService = inject(InterventionsService)
  private eventsService = inject(EventsService)
  private sectorService = inject(SectorService)
  private fb = inject(FormBuilder)
  private http = inject(HttpClient)

  /** Secteurs (avec géométrie) pour le boost d'affectation par secteur. */
  private allSectors: SectorModel[] = []

  // Extracted services for better separation of concerns
  public routeCalculationService = inject(RouteCalculationService)
  public indicatorService = inject(InterventionIndicatorService)
  public availabilityService = inject(AvailabilityDataService)
  public technicianSelectionService = inject(TechnicianSelectionService)

  // Add destroy subject for proper subscription cleanup
  private destroy$ = new Subject<void>()

  public affairLoading = true
  public loadingInterventions = false
  public loadingClosestTechnicians = false // Add loading state for closest technicians
  public rangeDates: FormControl<[Date, Date] | null>
  public filtersForm: FormGroup
  public usersForm: FormGroup
  public markersAffair = signal<markerInputModel[]>([])
  public markersClosest = signal<markerInputModel[]>([])
  public markersTechniciansHomes = signal<markerInputModel[]>([]) // Marqueurs pour les domiciles des techniciens
  public markersDailySchedule = signal<markerInputModel[]>([]) // Marqueurs pour les événements du jour avec horaires
  public departmentBoundaries = signal<DepartmentBoundaryInputModel[]>([]) // Limites départementales colorées par technicien
  public departmentGeoJson: any = null // GeoJSON des départements français
  public allMarkers = computed(() => [
    ...this.markersAffair(),
    ...this.markersClosest(),
    ...this.markersTechniciansHomes(),
    ...this.markersDailySchedule(),
  ])
  public closestInterventions$ = new Subscription()
  public closestTechnicians$ = new Subscription() // Add subscription for closest technicians
  public closestTechniciansByRoute$ = new Subscription() // Subscription pour les techniciens par route
  public affair$ = new Subscription()
  public closestInterventions: any[] = []
  public closestTechnicians: UserModel[] = [] // Add property to store closest technicians
  public closestTechniciansByRoute: {
    technician: UserModel
    distance_km: number
    duration_min: number | null
    distance_type: 'route' | 'haversine'
  }[] = [] // Techniciens proches par distance routière
  public loadingTechniciansByRoute = false // État de chargement
  public users$: Observable<UserModel[]> = new Observable()

  // Ajouter un état pour l'intervention survolée
  public hoveredInterventionId: string | null = null

  // Vérifier si les coordonnées du chantier sont valides
  public hasValidCoordinates = computed(() => {
    return (
      this.affair?.workSiteLatitude != null &&
      this.affair?.workSiteLongitude != null &&
      this.affair?.workSiteLatitude !== 0 &&
      this.affair?.workSiteLongitude !== 0
    )
  })

  // Add these properties
  public availableTechnicians: UserModel[] = []
  public selectedTechnicians: UserModel[] = []

  // Contrôle pour le MultiSelect (conservé pour compatibilité)
  public usersMultiSelectControl: FormControl

  // Contrôle pour le Select single
  public selectedTechnicianControl: FormControl<UserModel | null> =
    new FormControl(null)

  // Add ViewChild to access the map component directly
  @ViewChild(MapWithMarkersComponent) mapComponent: MapWithMarkersComponent

  public interventionsLimit: number = 10
  public interventionsSortBy: 'distance' | 'date' = 'distance' // Sort by distance (km) or date (earliest)
  public selectedIntervention: any = null // Store the selected intervention
  public selectedTechnicianDirect: UserModel | null = null // Store directly selected technician (when no intervention available)
  public selectedInterventionAvailabilities: any = null // Store availabilities for the selected intervention's technician
  public loadingSelectedInterventionAvailabilities = false // Loading state for selected intervention availabilities

  public preSelectedSlot: any = null // Store pre-selected time slot for next step

  // Daily route properties
  public dailyCalendarEvents: any[] = [] // Calendar events for the selected day
  public dailyRoute = signal<RouteInputModel | null>(null) // Route for the selected day (current)
  public projectedRoute = signal<RouteInputModel | null>(null) // Route including the affair to schedule
  public distanceLines = signal<DistanceLineInputModel[]>([]) // Distance lines from affair to events
  public loadingDailyRoute = false // Loading state for daily route
  public dailyRouteInfo: { distance_km: number; duration_min: number } | null =
    null // Route summary info
  public projectedRouteInfo: {
    distance_km: number
    duration_min: number
  } | null = null // Projected route summary info
  public isDailyScheduleExpanded = false // State for daily schedule panel expansion
  public distancesToAffair: Map<
    string,
    { distance_km: number; duration_min: number }
  > = new Map() // Distances from affair to each daily event
  public loadingDistancesToAffair = false // Loading state for distances calculation

  // Home departure and return info for current daily route (not projected)
  public currentRouteDeparture: {
    departureTime: Date // When to leave home
    firstEventTime: Date // First event start time
    travelMin: number // Travel duration in minutes from home
    isTight: boolean // Is departure before 7:30
  } | null = null

  public currentRouteReturn: {
    lastEventEndTime: Date // When last event ends
    returnTime: Date // When arriving home
    travelMin: number // Travel duration in minutes to home
  } | null = null

  // Projected return time when an affair is pre-selected at end of day
  public projectedRouteReturn: {
    affairEndTime: Date // When the affair ends
    returnTime: Date // When arriving home
    travelMin: number // Travel duration in minutes from affair to home
  } | null = null

  // Start/end location type for the current day (home or hotel)
  public startLocationType: 'home' | 'hotel' = 'home'
  public endLocationType: 'home' | 'hotel' = 'home'
  public startLocationCoords: { lat: number; lon: number } | null = null
  public endLocationCoords: { lat: number; lon: number } | null = null
  public startLocationCity: string = ''
  public endLocationCity: string = ''
  // Journée d'hôtel sans coordonnées : trajet non calculé mais "à prendre en compte"
  public startTravelUncounted: boolean = false
  public endTravelUncounted: boolean = false

  // Cache for daily data by technician/day to avoid recalculating when switching interventions
  private dailyDataCache: Map<
    string, // key: "technicianId_YYYY-MM-DD"
    {
      events: any[]
      routeInfo: { distance_km: number; duration_min: number } | null
      distancesToAffair: Map<
        string,
        { distance_km: number; duration_min: number }
      >
    }
  > = new Map()

  // Slot travel info - stores travel times for available slots
  public slotTravelInfo: Map<
    number,
    {
      travelFromPrevious: number | null // Minutes from previous intervention to affair
      travelToNext: number | null // Minutes from affair to next intervention
      travelFromHome: number | null // Minutes from technician's home to affair
      travelToHome: number | null // Minutes from affair to technician's home
      homeDepartureTime: Date | null // When the tech needs to leave home
      homeReturnTime: Date | null // When the tech would arrive home
      isFirstSlotOfDay: boolean // First slot of the day (depart from home)
      isLastSlotOfDay: boolean // Last slot of the day (return to home)
      homeDepartureIsTight: boolean // Home departure is early (before 7:00)
      homeReturnIsLate: boolean // Home return is late (after 19:30)
      totalTravelTime: number // Total travel time needed (both directions)
      availableTime: number // Time available in the slot (in minutes)
      effectiveAvailableTime: number // Time actually available after travel constraints
      requiredTime: number // Intervention duration + travel times
      isFeasible: boolean // Can fit the intervention + travel
      needsAdjustment: boolean // Would need to adjust neighboring interventions
      previousEvent: any | null // Previous busy slot/event
      nextEvent: any | null // Next busy slot/event
      arrivalDelay: number // How late the tech will arrive vs slot start (due to travel from previous)
      previousEventEnd: Date | null // When the previous event ends
      estimatedArrival: Date | null // When the tech will actually arrive at the affair
      nextEventStart: Date | null // When the next event starts
      latestDeparture: Date | null // Latest time tech must leave affair to reach next intervention
      previousEventDoNotShift: boolean | null // Previous event cannot be shifted
      nextEventDoNotShift: boolean | null // Next event cannot be shifted
      scenarioAddedKm: number | null // Extra km added by inserting at this position
      scenarioTotalKm: number | null // Total day route km with insertion
      scenarioAddedMin: number | null // Extra minutes added
      requiresShift: boolean // Requires shifting existing intervention
      shiftedEventId: number | null // ID of intervention to shift
      shiftedEventTitle: string | null // Title of intervention to shift
    }
  > = new Map()
  public loadingSlotTravelInfo = false

  // Cache for slot indicators by intervention ID (to show on all interventions of same day/tech)
  public interventionSlotIndicators: Map<
    number, // intervention ID
    {
      feasibleCount: number
      adjustableCount: number
      shiftCount: number
      totalSlots: number
      calculated: boolean
    }
  > = new Map()

  // Cache des résultats batch pour éviter les appels multiples
  public batchIndicatorsCache: Map<number, BatchInterventionResult> = new Map()
  public loadingBatchIndicators = false

  // Empty days (journées creuses) data
  public emptyDaysSlots: {
    date: string
    dayLabel: string
    slots: Array<{ start: string; end: string }>
    totalAvailableMinutes: number
  }[] = []
  public loadingEmptyDays = false
  private emptyDays$ = new Subscription()

  // Tab to show in the availabilities panel ('slots' or 'emptyDays')
  public preferredAvailabilityTab: 'slots' | 'emptyDays' = 'slots'
  // Flag: if batch indicators finish before closestTechniciansByRoute, defer auto-select
  private pendingAutoSelect = false
  // Flag to track if initial data has been loaded (to avoid reloading on step navigation)
  private hasLoadedInitialData = false

  // Add output for pre-selected slot
  @Output() slotPreSelected = new EventEmitter<any>()

  /**
   * Calculate total duration from selected products
   * @returns Total duration in minutes
   */
  get interventionDuration(): number {
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      return 120 // Default duration in minutes
    }

    return (
      this.selectedProducts.reduce((total, affairProduct) => {
        const productDuration = affairProduct.product?.duration || 0
        return total + productDuration
      }, 0) || 120
    ) // Fallback to 120 minutes if no duration found
  }

  /**
   * Build a Map of technician colors by email for the availabilities panel
   */
  get technicianColorsMap(): Map<string, string> {
    const colorsMap = new Map<string, string>()
    const allTechnicians = [
      ...(this.closestTechnicians || []),
      ...(this.users || []),
    ]
    allTechnicians.forEach((tech) => {
      if (tech.email) {
        colorsMap.set(tech.email, TechnicianTools.getColor(tech))
      }
    })
    return colorsMap
  }

  /**
   * Build a Map of technician initials by email for the availabilities panel
   */
  get technicianInitialsMap(): Map<string, string> {
    const initialsMap = new Map<string, string>()
    const allTechnicians = [
      ...(this.closestTechnicians || []),
      ...(this.users || []),
    ]
    allTechnicians.forEach((tech) => {
      if (tech.email) {
        initialsMap.set(tech.email, TechnicianTools.getInitials(tech))
      }
    })
    return initialsMap
  }

  constructor() {
    // Initialize forms in constructor
    this.filtersForm = this.fb.group({
      departmentsCode: [[]],
      search: [''],
      products: this.selectedProducts,
    })

    // Initialiser avec la semaine suivante
    const nextWeekRange = this.getNextWeekRange()
    this.rangeDates = this.fb.control<[Date, Date] | null>(nextWeekRange)

    this.usersForm = this.fb.group({
      users: this.fb.control<UserModel[]>([]),
    })

    // Initialiser le MultiSelect avec le FormControl du formulaire
    this.usersMultiSelectControl = this.usersForm.get('users') as FormControl
  }

  public ngOnInit() {
    // Charger le GeoJSON des départements français
    this.loadDepartmentGeoJson()

    // Charger les secteurs (géométrie) pour le boost d'affectation par secteur
    this.loadSectors()

    // Set markers if affair is available
    if (this.affair) {
      this.markersAffair.set([
        {
          coordinates: [
            this.affair.workSiteLatitude,
            this.affair.workSiteLongitude,
          ],
          iconColor: 'red',
          iconHtmlClass: 'pi pi-home',
          tooltipText: 'A programmer',
        },
      ])

      // Fetch closest technicians based on affair's department
      if (this.affair.workSiteDepartment) {
        this.fetchClosestTechnicians(this.affair.workSiteDepartment)
      }
    }

    // Setup form change listeners
    this.filtersForm.valueChanges.subscribe(() => {})

    // Émettre les dates initiales IMMÉDIATEMENT après l'initialisation
    setTimeout(() => {
      if (this.rangeDates.value?.length === 2) {
        this.dateRangeSelected.emit(this.rangeDates.value)
      }
    }, 0)

    // Puis configurer l'écouteur pour les changements futurs
    this.rangeDates.valueChanges.subscribe((dates) => {
      if (dates?.length === 2) {
        // Reset selected intervention and its availabilities when period changes
        this.clearSelectedIntervention()

        // Fetch interventions AND technicians by route in parallel
        this.fetchInitialDataParallel(dates)
        // Emit the date range to the parent component
        this.dateRangeSelected.emit(dates)

        // Note: On ne lance PAS fetchAvailabilitiesForTechnician ici
        // car fetchInitialDataParallel va appeler fetchBatchSlotIndicators
        // qui appelle autoSelectFirstAvailableIntervention
        // Si pas d'intervention proche, autoSelectClosestTechnicianByHome sera appelé
      }
    })

    this.usersForm.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.selectedTechnicians = value.users || []
        this.updateAvailableTechnicians()
        this.emitTechnicians()

        // Also fetch detailed availabilities for the selected technician(s)
        // to display in the right panel, but ONLY if no intervention is selected
        // (when an intervention is selected, its own availabilities are managed separately)
        const selectedUsers = value.users || []
        if (selectedUsers.length > 0 && !this.selectedIntervention) {
          // Fetch availabilities for the first selected technician
          this.fetchAvailabilitiesForTechnician(selectedUsers[0])
        }
      })

    // Réponse des utilisateurs filtrés
    this.filtersForm.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$) // Replace takeUntilDestroyed with takeUntil
      )
      .subscribe((filters) => {
        this.filtersChanged.emit(filters)
      })

    // Initial fetch of closest interventions AND technicians by route in parallel
    // This optimizes loading time by making concurrent API calls
    if (!this.hasLoadedInitialData) {
      this.fetchInitialDataParallel(this.rangeDates.value)
    }

    // Initialize available technicians from users input
    this.updateAvailableTechnicians()

    // Préparer les techniciens pour l'affichage
    this.prepareUserData()
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle reset flag changes
    if (changes['resetMap'] && changes['resetMap'].currentValue === true) {
      this.resetMapComponent()
    }

    // Restore date range from parent if provided
    if (changes['initialDateRange'] && this.initialDateRange) {
      const [start, end] = this.initialDateRange
      if (start && end) {
        this.rangeDates.setValue([new Date(start), new Date(end)], {
          emitEvent: false,
        })
      }
    }

    // Handle becoming active - refresh map when returning to this step
    if (
      changes['isActive'] &&
      changes['isActive'].currentValue === true &&
      !changes['isActive'].firstChange
    ) {
      this.refreshMapWhenVisible()
    }

    // Handle users changes
    if (changes['users'] && changes['users'].currentValue) {
      this.prepareUserData()
      this.updateAvailableTechnicians()
    }

    // Handle affair changes
    if (changes['affair'] && changes['affair'].currentValue) {
      this.updateAffairMarker()
    }

    if (changes['selectedProducts']) {
      this.filtersForm.patchValue({
        products: this.selectedProducts,
      })

      // Re-fetch batch indicators when products change (duration may have changed)
      if (
        this.selectedProducts &&
        this.selectedProducts.length > 0 &&
        this.rangeDates.value
      ) {
        this.fetchBatchSlotIndicators(this.rangeDates.value)
      }
    }
  }

  ngOnDestroy(): void {
    // Properly clean up all subscriptions
    this.destroy$.next()
    this.destroy$.complete()

    if (this.closestInterventions$) {
      this.closestInterventions$.unsubscribe()
    }

    if (this.closestTechnicians$) {
      this.closestTechnicians$.unsubscribe()
    }

    if (this.closestTechniciansByRoute$) {
      this.closestTechniciansByRoute$.unsubscribe()
    }

    if (this.affair$) {
      this.affair$.unsubscribe()
    }

    if (this.emptyDays$) {
      this.emptyDays$.unsubscribe()
    }
  }

  // Méthodes pour gérer le survol des interventions
  onInterventionMouseEnter(intervention: any) {
    this.hoveredInterventionId = intervention.id
    // Mettre à jour les marqueurs pour mettre en évidence celui survolé
    this.updateMarkers()
  }

  onInterventionMouseLeave() {
    this.hoveredInterventionId = null
    // Réinitialiser les marqueurs
    this.updateMarkers()
  }

  // Méthode pour mettre à jour tous les marqueurs
  updateMarkers() {
    if (this.closestInterventions) {
      const markers = this.closestInterventions.map((intervention) => {
        // Get the first technician if available
        const technician = intervention.usersTechnicians[0] || null

        // Get initials for this technician
        const techInitials = technician
          ? this.getTechnicianInitials(technician)
          : ''

        return {
          coordinates: [
            +intervention.affair.workSiteLatitude,
            +intervention.affair.workSiteLongitude,
          ],
          tooltipText: `${technician?.firstname || ''} ${technician?.lastname || ''}, le ${intervention.startDate}`,
          iconColor: technician?.color || '#666',
          highlighted: intervention.id === this.hoveredInterventionId,
          id: intervention.id,
          technicianInitials: techInitials,
          markerType: 'initials' as 'initials', // Use the new marker type for technician markers
        }
      })

      // Set the affair marker to be distinct from intervention markers
      if (this.affair) {
        this.markersAffair.set([
          {
            coordinates: [
              this.affair.workSiteLatitude,
              this.affair.workSiteLongitude,
            ],
            iconColor: 'red',
            iconHtmlClass: 'pi pi-home',
            tooltipText: `À programmer: ${this.affair.name || 'Affaire'}`,
            markerType: 'standard', // Keep the affair marker as standard
          },
        ])
      }

      this.markersClosest.set(markers)
    }
  }

  /**
   * Refresh both closest interventions and availabilities
   * Useful when calendar has been modified in parallel
   */
  refreshInterventionsAndAvailabilities(): void {
    // Clear caches
    this.batchIndicatorsCache.clear()
    this.interventionSlotIndicators.clear()
    this.dailyDataCache.clear()

    // Clear selection to force fresh data
    this.clearSelectedIntervention()

    // Refresh using parallel fetch
    this.fetchInitialDataParallel(this.rangeDates.value)
  }

  /**
   * Fetch closest interventions and technicians by route in parallel
   * Uses the unified getClosestWithIndicators endpoint for interventions
   * to get both interventions AND slot indicators in a single API call
   * @param dates Date range for fetching interventions
   */
  fetchInitialDataParallel(dates: [Date, Date] | null): void {
    if (!this.affair || !dates) {
      return
    }

    // Cancel previous subscriptions
    if (this.closestInterventions$) {
      this.closestInterventions$.unsubscribe()
    }
    if (this.closestTechniciansByRoute$) {
      this.closestTechniciansByRoute$.unsubscribe()
    }

    const dateFrom = this.formatDate(dates[0]) || '2025-03-01'
    const dateTo = this.formatDate(dates[1]) || '2025-03-30'
    const productsNames = this.selectedProducts.map((ap) => ap.product.name)

    // Set loading states
    this.loadingInterventions = true
    this.loadingTechniciansByRoute = true
    this.loadingBatchIndicators = true

    // Use the unified endpoint that combines getClosest + batchSlotIndicators
    const closestWithIndicators$ =
      this.interventionsService.getClosestWithIndicators({
        affair: {
          workSiteLatitude: this.affair.workSiteLatitude,
          workSiteLongitude: this.affair.workSiteLongitude,
        },
        dateFrom,
        dateTo,
        duration: this.interventionDuration,
        productsNames,
        number: this.interventionsLimit,
        sortBy: this.interventionsSortBy,
        excludeInterventionId: this.excludeInterventionId,
      })

    const closestTechniciansByRoute$ = this.hasValidCoordinates()
      ? this.usersService.getClosestByRoute({
          workSiteLatitude: this.affair.workSiteLatitude,
          workSiteLongitude: this.affair.workSiteLongitude,
          number: 10,
          maxDistance: 100,
          productsNames: productsNames.length > 0 ? productsNames : undefined,
        })
      : new Observable<{ technicians: any[] }>((subscriber) => {
          subscriber.next({ technicians: [] })
          subscriber.complete()
        })

    // Execute both API calls in parallel using forkJoin
    forkJoin({
      interventionsData: closestWithIndicators$,
      techniciansByRoute: closestTechniciansByRoute$,
    }).subscribe({
      next: (results) => {
        // Reset loading flags BEFORE processing (important for autoSelect logic)
        this.loadingInterventions = false
        this.loadingTechniciansByRoute = false
        this.loadingBatchIndicators = false

        console.log('[fetchInitialDataParallel] Response received:', {
          interventionsCount: results.interventionsData?.interventions?.length,
          techniciansByRouteCount:
            results.techniciansByRoute?.technicians?.length,
          status: results.interventionsData?.status,
        })

        // Process interventions with embedded indicators
        this.processClosestWithIndicators(results.interventionsData, dates)

        console.log('[fetchInitialDataParallel] After processing:', {
          closestInterventionsCount: this.closestInterventions?.length,
          batchIndicatorsCacheSize: this.batchIndicatorsCache.size,
          interventionSlotIndicatorsSize: this.interventionSlotIndicators.size,
        })

        // Process technicians by route — boost secteur : les techniciens rattachés
        // à un secteur contenant le chantier remontent en tête (priorité, pas contrainte).
        this.closestTechniciansByRoute =
          this.technicianSelectionService.prioritizeBySectors(
            results.techniciansByRoute.technicians,
            this.getMatchingSectorIds()
          )
        this.updateTechnicianHomeMarkers()
        this.techniciansByRouteChanged.emit(this.closestTechniciansByRoute)

        // Mark initial data as loaded to avoid reloading on step navigation
        this.hasLoadedInitialData = true

        // Auto-select best option (no need for pendingAutoSelect since both loaded together)
        this.autoSelectFirstAvailableIntervention()
      },
      error: (error) => {
        // Reset loading flags on error too
        this.loadingInterventions = false
        this.loadingTechniciansByRoute = false
        this.loadingBatchIndicators = false
        console.error('Error fetching initial data in parallel:', error)
      },
    })
  }

  /**
   * Charge (une fois) la liste des secteurs avec leur géométrie, nécessaire au
   * boost d'affectation par secteur (test chantier-dans-polygone).
   */
  private loadSectors(): void {
    this.sectorService
      .getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sectors) => {
          this.allSectors = sectors
        },
        error: () => {
          this.allSectors = []
        },
      })
  }

  /**
   * Renvoie les ids des secteurs dont le polygone contient le chantier de l'affaire.
   * Vide si les coordonnées sont invalides ou si aucun secteur ne correspond.
   */
  private getMatchingSectorIds(): number[] {
    if (!this.hasValidCoordinates() || !this.allSectors.length) {
      return []
    }

    const lat = this.affair.workSiteLatitude
    const lng = this.affair.workSiteLongitude

    return this.allSectors
      .filter((sector) => isPointInSector(lat, lng, sector.geometry))
      .map((sector) => sector.id)
  }

  /**
   * Process the combined response from getClosestWithIndicators
   * Populates both closestInterventions and batchIndicatorsCache from a single response
   */
  private processClosestWithIndicators(
    response: ClosestWithIndicatorsResponse,
    dates: [Date, Date]
  ): void {
    if (response.status !== 'success') {
      console.error('Error in closestWithIndicators response:', response)
      return
    }

    // Extract interventions (without the slot_indicators for the list display)
    let extractedInterventions = response.interventions.map((intervention) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { slot_indicators, ...interventionData } = intervention
      return interventionData
    })

    // Filter out excluded intervention if specified (for reschedule)
    if (this.excludeInterventionId) {
      extractedInterventions = extractedInterventions.filter(
        (i) => i.id !== this.excludeInterventionId
      )
    }

    this.closestInterventions = extractedInterventions
    this.closestInterventionsChanged.emit(this.closestInterventions)
    this.updateMarkers()

    // Populate the indicators caches from the embedded data
    this.batchIndicatorsCache.clear()
    this.interventionSlotIndicators.clear()

    response.interventions.forEach(
      (intervention: InterventionWithIndicators) => {
        if (intervention.slot_indicators && intervention.id) {
          // Create BatchInterventionResult format for batchIndicatorsCache
          const batchResult: BatchInterventionResult = {
            intervention_id: intervention.id,
            technician_id: intervention.usersTechnicians?.[0]?.id || 0,
            date: intervention.startDate,
            feasible_slots: intervention.slot_indicators.feasible_slots,
            adjustable_slots: intervention.slot_indicators.adjustable_slots,
            total_slots: intervention.slot_indicators.total_slots,
            distance_to_affair_km:
              intervention.slot_indicators.distance_to_affair_km,
            duration_to_affair_min:
              intervention.slot_indicators.duration_to_affair_min,
            travel_from_home_min:
              intervention.slot_indicators.travel_from_home_min ?? null,
            start_location_type:
              intervention.slot_indicators.start_location_type ?? 'home',
            end_location_type:
              intervention.slot_indicators.end_location_type ?? 'home',
            start_location_lat:
              intervention.slot_indicators.start_location_lat ?? null,
            start_location_lon:
              intervention.slot_indicators.start_location_lon ?? null,
            end_location_lat:
              intervention.slot_indicators.end_location_lat ?? null,
            end_location_lon:
              intervention.slot_indicators.end_location_lon ?? null,
            start_location_city:
              intervention.slot_indicators.start_location_city ?? null,
            end_location_city:
              intervention.slot_indicators.end_location_city ?? null,
            start_travel_uncounted:
              intervention.slot_indicators.start_travel_uncounted ?? false,
            end_travel_uncounted:
              intervention.slot_indicators.end_travel_uncounted ?? false,
            slots: intervention.slot_indicators.slots,
            calendar_events: intervention.slot_indicators.calendar_events ?? [],
          }
          this.batchIndicatorsCache.set(intervention.id, batchResult)

          // Calculate constrained counts and populate interventionSlotIndicators
          const constrainedCounts =
            this.calculateConstrainedSlotCounts(batchResult)
          this.interventionSlotIndicators.set(intervention.id, {
            feasibleCount: constrainedCounts.feasibleCount,
            adjustableCount: constrainedCounts.adjustableCount,
            shiftCount: constrainedCounts.shiftCount,
            totalSlots: constrainedCounts.totalSlots,
            calculated: true,
          })
        }
      }
    )

    // Emit indicators for persistence
    this.emitBatchIndicators()
  }

  // Modifier la méthode fetchClosestInterventions pour utiliser updateMarkers
  fetchClosestInterventions(dates: [Date, Date] | null) {
    if (!this.affair || !dates) {
      return
    }

    if (this.closestInterventions$) {
      this.closestInterventions$.unsubscribe()
    }

    const dateFrom = this.formatDate(dates[0]) || '2025-03-01'
    const dateTo = this.formatDate(dates[1]) || '2025-03-30'
    const productsNames = this.selectedProducts.map((ap) => ap.product.name)

    this.loadingInterventions = true
    this.closestInterventions$ = this.interventionsService
      .getClosest({
        dateFrom,
        dateTo,
        productsNames,
        number: this.interventionsLimit,
        sortBy: this.interventionsSortBy,
        affair: {
          workSiteLatitude: this.affair.workSiteLatitude,
          workSiteLongitude: this.affair.workSiteLongitude,
        },
        excludeInterventionId: this.excludeInterventionId,
      })
      .pipe(finalize(() => (this.loadingInterventions = false)))
      .subscribe((interventions) => {
        // Filter out excluded intervention if specified
        this.closestInterventions = this.excludeInterventionId
          ? interventions.filter((i) => i.id !== this.excludeInterventionId)
          : interventions
        // Emit the closest interventions when they are loaded
        this.closestInterventionsChanged.emit(this.closestInterventions)

        // Utiliser la méthode updateMarkers pour créer les marqueurs
        this.updateMarkers()

        // Fetch batch slot indicators for all interventions (optimized)
        // This single call replaces both individual slot calculations AND technician availabilities
        this.fetchBatchSlotIndicators(dates)
      })
  }

  /**
   * Fetch slot indicators for all interventions in batch (optimized)
   * Delegates to InterventionIndicatorService
   */
  fetchBatchSlotIndicators(dates: [Date, Date]): void {
    if (!this.affair) {
      return
    }

    // Si aucune intervention proche, appeler directement le fallback (getAvailabilities)
    if (!this.closestInterventions || this.closestInterventions.length === 0) {
      const selectedUsers = this.usersForm.value.users || []
      if (selectedUsers.length > 0) {
        this.fetchAvailabilitiesForTechnician(selectedUsers[0])
      } else {
        this.autoSelectClosestTechnicianByHome()
      }
      return
    }

    const dateFrom = this.formatDate(dates[0])
    const dateTo = this.formatDate(dates[1])
    const productsNames = this.selectedProducts.map(
      (ap: any) => ap.product.name
    )

    this.loadingBatchIndicators = true

    // Delegate to service
    this.indicatorService
      .fetchBatchSlotIndicators(
        {
          workSiteLatitude: this.affair.workSiteLatitude,
          workSiteLongitude: this.affair.workSiteLongitude,
        },
        dateFrom || '',
        dateTo || '',
        this.interventionDuration,
        productsNames,
        this.closestInterventions,
        this.interventionsLimit
      )
      .subscribe({
        next: (result) => {
          this.loadingBatchIndicators = false

          // Sync local caches with service
          this.batchIndicatorsCache = new Map(
            this.indicatorService.batchIndicatorsCache
          )
          this.interventionSlotIndicators = new Map(
            this.indicatorService.interventionSlotIndicators
          )

          if (result.hasInterventions && !this.selectedIntervention) {
            this.autoSelectFirstAvailableIntervention()
          } else if (this.selectedIntervention) {
            // Mettre à jour les disponibilités de l'intervention déjà sélectionnée depuis le cache
            this.fetchSelectedInterventionAvailabilities(
              this.selectedIntervention
            )
          }

          // Emit indicators for persistence
          this.emitBatchIndicators()
        },
        error: () => {
          this.loadingBatchIndicators = false
        },
      })
  }

  /**
   * Emit batch indicators for persistence in parent
   */
  private emitBatchIndicators(): void {
    const indicatorsObject = this.indicatorService.exportBatchIndicators()
    this.batchIndicatorsChanged.emit(indicatorsObject)
  }

  /**
   * Auto-select the best option: closest intervention OR a nearby-home technician with empty days.
   *
   * Priority logic:
   * 1. Find the best (closest) intervention with feasible slots
   * 2. Compare its travel time to the closest technician by home distance
   * 3. If a technician lives closer to the job site than the best intervention's location,
   *    prefer that technician (show their empty days / availabilities)
   * 4. Otherwise, select the closest intervention as before
   */
  autoSelectFirstAvailableIntervention(): void {
    console.log('[autoSelectFirstAvailableIntervention] Starting...')

    // If closestTechniciansByRoute is still loading, defer this call
    if (this.loadingTechniciansByRoute) {
      console.log(
        '[autoSelectFirstAvailableIntervention] Deferred - techniciansByRoute still loading'
      )
      this.pendingAutoSelect = true
      return
    }

    // Use selection service to determine the best auto-selection
    const selectedUsers = this.usersForm.value.users || []
    const result = this.technicianSelectionService.determineAutoSelection(
      this.closestInterventions || [],
      this.interventionSlotIndicators,
      this.closestTechniciansByRoute || [],
      this.users || [],
      selectedUsers
    )

    console.log(
      '[autoSelectFirstAvailableIntervention] Selection result:',
      result
    )

    // Apply the selection result
    switch (result.type) {
      case 'home_technician':
        if (result.technician) {
          console.log(
            '[autoSelectFirstAvailableIntervention] Selecting home technician:',
            result.technician.email
          )
          this.addTechnicianToSelection(result.technician)
          this.selectedTechnicians = [result.technician]
          this.preferredAvailabilityTab = result.preferredTab
          this.fetchAvailabilitiesForTechnician(result.technician)
        }
        break

      case 'intervention':
        if (result.intervention) {
          console.log(
            '[autoSelectFirstAvailableIntervention] Selecting intervention:',
            result.intervention.id
          )
          this.preferredAvailabilityTab = result.preferredTab
          this.onSelectIntervention(result.intervention)
        }
        break

      case 'selected_user':
        if (result.technician) {
          this.fetchAvailabilitiesForTechnician(result.technician)
        }
        break

      case 'none':
      default:
        console.log(
          '[autoSelectFirstAvailableIntervention] No selection, falling back to closest home'
        )
        this.autoSelectClosestTechnicianByHome()
        break
    }
  }

  /**
   * Auto-select the closest technician by home distance and display all their availabilities
   * This is used when no interventions are available nearby
   */
  autoSelectClosestTechnicianByHome(): void {
    const userToSelect =
      this.technicianSelectionService.findClosestTechnicianByHome(
        this.closestTechniciansByRoute || [],
        this.users || []
      )

    if (!userToSelect) {
      return
    }

    // Select this technician in the form (using single select method)
    this.addTechnicianToSelection(userToSelect)
    this.selectedTechnicians = [userToSelect]

    // Fetch and display all availabilities for this technician
    this.fetchAvailabilitiesForTechnician(userToSelect)
  }

  /**
   * Fetch all availabilities for a specific technician across the selected date range
   * @param technician The technician to fetch availabilities for
   */
  fetchAvailabilitiesForTechnician(technician: UserModel): void {
    if (!this.rangeDates.value || this.rangeDates.value.length !== 2) {
      this.selectedInterventionAvailabilities = null
      this.selectedTechnicianDirect = null
      return
    }

    // Store the directly selected technician
    this.selectedTechnicianDirect = technician
    // Clear any selected intervention since we're selecting directly
    this.selectedIntervention = null

    // Fetch empty days for this technician
    this.fetchTechnicianEmptyDays(technician.id, technician.email)

    const startOfRange = new Date(this.rangeDates.value[0])
    startOfRange.setHours(8, 0, 0, 0)
    const endOfRange = new Date(this.rangeDates.value[1])
    endOfRange.setHours(18, 0, 0, 0)

    const startDate = this.formatDateTimeString(startOfRange, '08:00:00')
    const endDate = this.formatDateTimeString(endOfRange, '18:00:00')

    const requestData = {
      start_date: startDate,
      end_date: endDate,
      duration: this.interventionDuration,
      user_ids: [technician.id],
    }

    this.loadingSelectedInterventionAvailabilities = true
    this.usersService
      .getAvailabilities(requestData)
      .pipe(
        finalize(() => (this.loadingSelectedInterventionAvailabilities = false))
      )
      .subscribe({
        next: (availabilities) => {
          if (availabilities.length > 0) {
            const rawAvailabilities = availabilities[0]

            // Découper les plages en créneaux de la durée d'intervention
            const rawSlots =
              rawAvailabilities.availabilities?.available_slots ||
              rawAvailabilities.daily_availabilities?.flatMap(
                (day: any) => day.available_slots || []
              ) ||
              []

            const chunkedSlots = AvailabilityTools.splitSlotsIntoDurationChunks(
              rawSlots,
              this.interventionDuration
            )

            // Mettre à jour les disponibilités avec les créneaux découpés
            this.selectedInterventionAvailabilities = {
              ...rawAvailabilities,
              availabilities: {
                ...rawAvailabilities.availabilities,
                available_slots: chunkedSlots,
              },
            }
          } else {
            this.selectedInterventionAvailabilities = null
          }
        },
        error: (error) => {
          console.error(
            'Erreur lors de la récupération des disponibilités du technicien:',
            error
          )
          this.selectedInterventionAvailabilities = null
        },
      })
  }

  /**
   * Fetch empty days (journées creuses) for a technician across the selected date range.
   * An empty day is one where the technician has no busy_slots (no interventions or appointments).
   * @param technicianId The technician's user ID
   * @param techEmail The technician's email (for slot selection)
   */
  fetchTechnicianEmptyDays(technicianId: number, techEmail?: string): void {
    if (!this.rangeDates.value || this.rangeDates.value.length !== 2) {
      this.emptyDaysSlots = []
      return
    }

    // Cancel previous empty days request
    if (this.emptyDays$) {
      this.emptyDays$.unsubscribe()
    }

    const startOfRange = new Date(this.rangeDates.value[0])
    startOfRange.setHours(8, 0, 0, 0)
    const endOfRange = new Date(this.rangeDates.value[1])
    endOfRange.setHours(18, 0, 0, 0)

    const startDate = this.formatDateTimeString(startOfRange, '08:00:00')
    const endDate = this.formatDateTimeString(endOfRange, '18:00:00')

    const requestData = {
      start_date: startDate,
      end_date: endDate,
      duration: this.interventionDuration,
      user_ids: [technicianId],
      group_by_day: true,
    }

    this.loadingEmptyDays = true
    this.emptyDays$ = this.usersService
      .getAvailabilities(requestData)
      .pipe(finalize(() => (this.loadingEmptyDays = false)))
      .subscribe({
        next: (availabilities) => {
          if (
            availabilities.length > 0 &&
            availabilities[0].daily_availabilities
          ) {
            const dailyData = availabilities[0].daily_availabilities
            const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
            const emptyDays: typeof this.emptyDaysSlots = []

            for (const [dateStr, dayAvailabilities] of Object.entries(
              dailyData as Record<
                string,
                {
                  available_slots: Array<{ start: string; end: string }>
                  busy_slots: Array<{
                    start: string
                    end: string
                    title: string
                    type: string
                  }>
                }
              >
            )) {
              const busySlots = dayAvailabilities.busy_slots || []
              const availableSlots = dayAvailabilities.available_slots || []

              // A day is "empty" if it has no busy slots and has available slots
              if (busySlots.length === 0 && availableSlots.length > 0) {
                const dateObj = new Date(dateStr + 'T00:00:00')
                const dayName = dayNames[dateObj.getDay()]
                const dd = String(dateObj.getDate()).padStart(2, '0')
                const mm = String(dateObj.getMonth() + 1).padStart(2, '0')

                const totalMinutes = availableSlots.reduce((sum, slot) => {
                  return sum + AvailabilityTools.getSlotDurationMinutes(slot)
                }, 0)

                emptyDays.push({
                  date: dateStr,
                  dayLabel: `${dayName} ${dd}/${mm}`,
                  slots: availableSlots,
                  totalAvailableMinutes: totalMinutes,
                })
              }
            }

            // Sort by date
            emptyDays.sort(
              (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
            )
            this.emptyDaysSlots = emptyDays
          } else {
            this.emptyDaysSlots = []
          }
        },
        error: (error) => {
          console.error(
            'Erreur lors de la récupération des journées creuses:',
            error
          )
          this.emptyDaysSlots = []
        },
      })
  }

  // New method to fetch closest technicians
  fetchClosestTechnicians(departmentCode: string) {
    if (!departmentCode) {
      return
    }

    if (this.closestTechnicians$) {
      this.closestTechnicians$.unsubscribe()
    }

    this.loadingClosestTechnicians = true
    const productsNames = this.selectedProducts.map((ap) => ap.product.name)
    this.closestTechnicians$ = this.usersService
      .getClosest(departmentCode, productsNames)
      .pipe(finalize(() => (this.loadingClosestTechnicians = false)))
      .subscribe((technicians) => {
        this.closestTechnicians = technicians
      })
  }

  /**
   * Met à jour les marqueurs des domiciles des techniciens sur la carte
   */
  updateTechnicianHomeMarkers() {
    if (
      !this.closestTechniciansByRoute ||
      this.closestTechniciansByRoute.length === 0
    ) {
      this.markersTechniciansHomes.set([])
      return
    }

    const homeMarkers = this.closestTechniciansByRoute
      .filter(
        (item) =>
          item.technician.homeLatitude &&
          item.technician.homeLongitude &&
          item.technician.homeLatitude !== 0 &&
          item.technician.homeLongitude !== 0
      )
      .map((item) => {
        const initials = this.getTechnicianInitials(item.technician)
        const distanceInfo =
          item.distance_type === 'route'
            ? `${item.distance_km} km (${item.duration_min} min)`
            : `${item.distance_km} km (vol d'oiseau)`

        return {
          coordinates: [
            item.technician.homeLatitude,
            item.technician.homeLongitude,
          ],
          tooltipText: `🏠 ${item.technician.firstname} ${item.technician.lastname} - ${distanceInfo}`,
          iconColor: item.technician.color || '#666',
          technicianInitials: initials,
          markerType: 'home' as 'home', // Nouveau type de marqueur pour les domiciles
          id: `home-${item.technician.id}`,
        }
      })

    this.markersTechniciansHomes.set(homeMarkers)

    // Mettre à jour les limites départementales avec les couleurs des techniciens
    this.updateDepartmentBoundaries()
  }

  /**
   * Charge le fichier GeoJSON des départements français
   */
  private loadDepartmentGeoJson() {
    this.http.get('france.json').subscribe({
      next: (geoJson) => {
        this.departmentGeoJson = geoJson
        // Si les techniciens sont déjà chargés, mettre à jour les boundaries
        this.updateDepartmentBoundaries()
      },
      error: (err) => {
        console.error(
          'Erreur lors du chargement du GeoJSON des départements:',
          err
        )
      },
    })
  }

  /**
   * Construit les données de limites départementales à partir des techniciens proches
   * Chaque département est coloré avec la couleur du technicien affecté
   */
  private updateDepartmentBoundaries() {
    if (
      !this.closestTechniciansByRoute ||
      this.closestTechniciansByRoute.length === 0 ||
      !this.departmentGeoJson
    ) {
      this.departmentBoundaries.set([])
      return
    }

    const boundaries: DepartmentBoundaryInputModel[] = []

    this.closestTechniciansByRoute.forEach((item) => {
      const tech = item.technician
      const color = TechnicianTools.getColor(tech)
      const initials = this.getTechnicianInitials(tech)
      const fullName = `${tech.firstname} ${tech.lastname}`

      if (tech.departments && tech.departments.length > 0) {
        tech.departments.forEach((dept) => {
          boundaries.push({
            departmentCode: dept.code,
            color: color,
            technicianInitials: initials,
            technicianName: fullName,
          })
        })
      }
    })

    this.departmentBoundaries.set(boundaries)
  }

  /**
   * Récupère les disponibilités avec temps de trajet pour une intervention sélectionnée
   * Utilise le cache batch si disponible, sinon appelle singleAvailabilityWithTravel en fallback
   */
  fetchSelectedInterventionAvailabilities(intervention: any) {
    if (
      !intervention ||
      !intervention.usersTechnicians ||
      intervention.usersTechnicians.length === 0
    ) {
      this.selectedInterventionAvailabilities = null
      this.loadingSelectedInterventionAvailabilities = false
      this.loadingSlotTravelInfo = false
      return
    }

    if (!this.affair || !this.hasValidCoordinates()) {
      this.selectedInterventionAvailabilities = null
      this.loadingSelectedInterventionAvailabilities = false
      this.loadingSlotTravelInfo = false
      return
    }

    // Vérifier si les données sont déjà dans le cache batch
    const cachedData = this.batchIndicatorsCache.get(intervention.id)

    if (cachedData && cachedData.slots) {
      // Utiliser les données du cache batch (pas d'appel API supplémentaire)
      // Même si 0 slots, on utilise le résultat du batch pour la cohérence
      this.startLocationType = cachedData.start_location_type || 'home'
      this.endLocationType = cachedData.end_location_type || 'home'
      this.startLocationCoords =
        cachedData.start_location_lat && cachedData.start_location_lon
          ? {
              lat: cachedData.start_location_lat,
              lon: cachedData.start_location_lon,
            }
          : null
      this.endLocationCoords =
        cachedData.end_location_lat && cachedData.end_location_lon
          ? {
              lat: cachedData.end_location_lat,
              lon: cachedData.end_location_lon,
            }
          : null
      this.startLocationCity = cachedData.start_location_city || ''
      this.endLocationCity = cachedData.end_location_city || ''
      this.startTravelUncounted = cachedData.start_travel_uncounted ?? false
      this.endTravelUncounted = cachedData.end_travel_uncounted ?? false
      this.populateAvailabilitiesFromBatchCache(intervention, cachedData)
      return
    }

    // Si pas de cache, les données ne sont pas encore disponibles
    // Le batch est en cours de chargement, on attend
    if (this.loadingBatchIndicators) {
      this.loadingSelectedInterventionAvailabilities = true
      this.loadingSlotTravelInfo = true
      return
    }

    // Fallback: appeler singleAvailabilityWithTravel si le cache est vide
    this.loadingSelectedInterventionAvailabilities = true
    this.loadingSlotTravelInfo = true

    this.interventionsService
      .singleAvailabilityWithTravel({
        affair: {
          workSiteLatitude: this.affair.workSiteLatitude,
          workSiteLongitude: this.affair.workSiteLongitude,
        },
        intervention: {
          id: intervention.id,
          technician_id: intervention.usersTechnicians?.[0]?.id,
          startDate: intervention.startDate,
          workSiteLatitude: intervention.affair?.workSiteLatitude,
          workSiteLongitude: intervention.affair?.workSiteLongitude,
          usersTechnicians: intervention.usersTechnicians,
        },
        duration: this.interventionDuration,
      })
      .pipe(
        finalize(() => {
          this.loadingSelectedInterventionAvailabilities = false
          this.loadingSlotTravelInfo = false
        })
      )
      .subscribe({
        next: (response) => {
          if (response.status === 'success' && response.slots) {
            // Store location type info from the response
            this.startLocationType = response.start_location_type || 'home'
            this.endLocationType = response.end_location_type || 'home'
            this.startLocationCoords =
              response.start_location_lat && response.start_location_lon
                ? {
                    lat: response.start_location_lat,
                    lon: response.start_location_lon,
                  }
                : null
            this.endLocationCoords =
              response.end_location_lat && response.end_location_lon
                ? {
                    lat: response.end_location_lat,
                    lon: response.end_location_lon,
                  }
                : null
            this.startLocationCity = response.start_location_city || ''
            this.endLocationCity = response.end_location_city || ''
            this.startTravelUncounted = response.start_travel_uncounted ?? false
            this.endTravelUncounted = response.end_travel_uncounted ?? false

            // Create a BatchInterventionResult from the response
            const batchResult: BatchInterventionResult = {
              intervention_id: intervention.id,
              technician_id: response.technician_id,
              date: response.date,
              feasible_slots: response.feasible_slots,
              adjustable_slots: response.adjustable_slots,
              total_slots: response.total_slots,
              distance_to_affair_km: response.distance_to_affair_km,
              duration_to_affair_min: response.duration_to_affair_min,
              travel_from_home_min: null,
              start_location_type: response.start_location_type,
              end_location_type: response.end_location_type,
              start_location_lat: response.start_location_lat,
              start_location_lon: response.start_location_lon,
              end_location_lat: response.end_location_lat,
              end_location_lon: response.end_location_lon,
              start_location_city: response.start_location_city,
              end_location_city: response.end_location_city,
              start_travel_uncounted: response.start_travel_uncounted,
              end_travel_uncounted: response.end_travel_uncounted,
              slots: response.slots,
            }

            // Cache the result for future use
            this.batchIndicatorsCache.set(intervention.id, batchResult)

            // Populate the availabilities
            this.populateAvailabilitiesFromBatchCache(intervention, batchResult)
          } else {
            this.selectedInterventionAvailabilities = null
          }
        },
        error: (error) => {
          console.error('Error fetching single availability:', error)
          this.selectedInterventionAvailabilities = null
        },
      })
  }

  /**
   * Populate availabilities from batch cache data
   * Évite les appels API redondants en utilisant les données déjà récupérées
   */
  private populateAvailabilitiesFromBatchCache(
    intervention: any,
    cachedData: BatchInterventionResult
  ): void {
    // Convertir les slots du cache en format attendu par le frontend
    const availableSlots = cachedData.slots.map((slot) => ({
      start: slot.start,
      end: slot.end,
      available_minutes: slot.available_minutes,
    }))

    // Récupérer les informations du technicien depuis l'intervention
    const tech = intervention.usersTechnicians?.[0]
    const techEmail = tech?.email || ''
    const techName = tech
      ? `${tech.firstname || ''} ${tech.lastname || ''}`.trim()
      : ''

    // Stocker les disponibilités dans le format attendu par le template
    this.selectedInterventionAvailabilities = {
      userId: cachedData.technician_id,
      user_email: techEmail,
      user_name: techName,
      availabilities: {
        available_slots: availableSlots,
        busy_slots: (cachedData.calendar_events || []).map((evt) => ({
          title: evt.title || '',
          start: evt.start,
          end: evt.end,
          type: evt.type || 'intervention',
          doNotShift: evt.doNotShift ?? false,
        })),
      },
    }

    // Convertir les données de trajet du cache en SlotTravelInfo pour chaque slot
    this.slotTravelInfo.clear()
    cachedData.slots.forEach((slot, index) => {
      this.slotTravelInfo.set(index, {
        travelFromPrevious: slot.travel_from_previous_min,
        travelToNext: slot.travel_to_next_min,
        travelFromHome: slot.travel_from_home_min ?? null,
        travelToHome: slot.travel_to_home_min ?? null,
        homeDepartureTime: slot.home_departure_time
          ? new Date(slot.home_departure_time)
          : null,
        homeReturnTime: slot.home_return_time
          ? new Date(slot.home_return_time)
          : null,
        isFirstSlotOfDay: slot.is_first_slot_of_day ?? false,
        isLastSlotOfDay: slot.is_last_slot_of_day ?? false,
        homeDepartureIsTight: slot.home_departure_is_tight ?? false,
        homeReturnIsLate: slot.home_return_is_late ?? false,
        totalTravelTime:
          (slot.travel_from_previous_min || 0) + (slot.travel_to_next_min || 0),
        availableTime: slot.available_minutes,
        effectiveAvailableTime: slot.effective_available_minutes,
        requiredTime: this.interventionDuration,
        isFeasible: slot.is_feasible,
        needsAdjustment: slot.needs_adjustment,
        previousEvent: null,
        nextEvent: null,
        arrivalDelay: 0,
        previousEventEnd: null,
        nextEventStart: null,
        estimatedArrival: slot.estimated_arrival
          ? new Date(slot.estimated_arrival)
          : new Date(slot.start),
        latestDeparture: slot.latest_departure
          ? new Date(slot.latest_departure)
          : new Date(slot.end),
        previousEventDoNotShift: slot.previous_event_do_not_shift ?? null,
        nextEventDoNotShift: slot.next_event_do_not_shift ?? null,
        scenarioAddedKm: slot.scenario_added_km ?? null,
        scenarioTotalKm: slot.scenario_total_km ?? null,
        scenarioAddedMin: slot.scenario_added_min ?? null,
        requiresShift: slot.requires_shift ?? false,
        shiftedEventId: slot.shifted_event_id ?? null,
        shiftedEventTitle: slot.shifted_event_title ?? null,
      })
    })

    this.loadingSelectedInterventionAvailabilities = false
    this.loadingSlotTravelInfo = false
  }

  // Helper method to format date and time for API
  formatDateTimeString(date: Date, time: string): string {
    return DateTools.formatDateTimeString(date, time)
  }

  getNextWeekRange(): [Date, Date] {
    return DateTools.getNextWeekRange()
  }

  // Formatage de la date au format yyyy-mm-dd
  formatDate(date: Date) {
    return DateTools.formatDateYMD(date)
  }

  formatTwoDigits(n: number) {
    return DateTools.formatTwoDigits(n)
  }

  // Fonction pour supprimer un utilisateur sélectionné
  // Émettre les techniciens sélectionnés
  emitTechnicians() {
    const selectedTechnicians: UserModel[] =
      this.usersForm.controls['users'].value ?? []
    this.techniciansSelected.emit(selectedTechnicians)
  }

  // Modify the method that handles intervention selection
  onSelectIntervention(intervention: any): void {
    // Store the selected intervention and highlight it
    this.selectedIntervention = intervention
    this.selectedInterventionChanged.emit(intervention) // Emit for persistence
    // Clear direct technician selection since we're selecting via intervention
    this.selectedTechnicianDirect = null
    this.highlightIntervention(intervention)

    // Pre-fill technician selection (take first if multiple)
    if (
      intervention.usersTechnicians &&
      intervention.usersTechnicians.length > 0
    ) {
      const firstTech = intervention.usersTechnicians[0]
      const techUser = this.users?.find(
        (u: UserModel) => u.email === firstTech.email
      )
      if (techUser) {
        this.addTechnicianToSelection(techUser)
      }
    }

    // Switch to slots tab when selecting an intervention
    this.preferredAvailabilityTab = 'slots'

    // Clear previous route and calendar events immediately to avoid showing stale data
    this.clearDailyRoute()

    // Clear previous slot travel info immediately to avoid showing stale data
    this.slotTravelInfo.clear()
    this.loadingSlotTravelInfo = true

    // Clear pre-selected slot when changing intervention
    this.clearPreSelectedSlot()

    // Fetch availabilities for this intervention's technician
    this.fetchSelectedInterventionAvailabilities(intervention)

    // Fetch empty days for this technician
    if (
      intervention.usersTechnicians &&
      intervention.usersTechnicians.length > 0
    ) {
      const tech = intervention.usersTechnicians[0]
      const techUser = this.users?.find(
        (u: UserModel) => u.email === tech.email
      )
      if (techUser) {
        this.fetchTechnicianEmptyDays(techUser.id, techUser.email)
      }
    }

    // Fetch daily calendar events and calculate route for this day
    this.fetchDailyCalendarEventsAndRoute(intervention)

    // If the intervention provides technicians, replace current selection with them
    if (
      intervention.usersTechnicians &&
      intervention.usersTechnicians.length > 0
    ) {
      // Map intervention technicians to the canonical user objects
      const matchedUsers = TechnicianTools.mapInterventionTechnicians(
        intervention.usersTechnicians,
        this.users
      )

      // Update the FormControl
      this.usersForm.get('users')?.setValue(matchedUsers)
      this.selectedTechnicians = matchedUsers
      this.emitTechnicians()
    } else {
      // No technicians on the intervention: clear selection
      this.usersForm.get('users')?.setValue([])
      this.selectedTechnicians = []
      this.emitTechnicians()
    }
  } // Helper method to highlight intervention
  highlightIntervention(intervention: any): void {
    // Remove previous highlighting
    document.querySelectorAll('.highlighted-intervention').forEach((el) => {
      el.classList.remove('highlighted-intervention')
    })

    // Add highlighting to the selected intervention
    const interventionElements = document.querySelectorAll(
      `[data-intervention-id="${intervention.id}"]`
    )
    interventionElements.forEach((el) => {
      el.classList.add('highlighted-intervention')
    })
  }

  // Method to clear selected intervention
  clearSelectedIntervention(): void {
    this.selectedIntervention = null
    this.selectedTechnicianDirect = null
    this.selectedInterventionAvailabilities = null

    // Clear empty days data
    this.emptyDaysSlots = []

    // Reset tab preference
    this.preferredAvailabilityTab = 'slots'

    // Clear pre-selected slot when clearing intervention
    this.clearPreSelectedSlot()

    // Clear daily route
    this.clearDailyRoute()

    // Clear daily data cache when clearing selection (new week/period)
    this.dailyDataCache.clear()

    // Note: We intentionally do NOT clear batchIndicatorsCache and interventionSlotIndicators here
    // These should persist for the current date range and only be cleared on:
    // - Date range change (via fetchBatchSlotIndicators)
    // - Explicit refresh (via refreshInterventionsAndAvailabilities)

    // Remove highlighting from all interventions
    document.querySelectorAll('.highlighted-intervention').forEach((el) => {
      el.classList.remove('highlighted-intervention')
    })
  }

  /**
   * Clear the daily route and calendar events
   * Delegates to RouteCalculationService
   */
  clearDailyRoute(): void {
    this.dailyCalendarEvents = []
    this.markersDailySchedule.set([])

    // Reset location state
    this.startLocationType = 'home'
    this.endLocationType = 'home'
    this.startLocationCoords = null
    this.endLocationCoords = null
    this.startLocationCity = ''
    this.endLocationCity = ''
    this.startTravelUncounted = false
    this.endTravelUncounted = false

    // Delegate to service
    this.routeCalculationService.clearDailyRoute()

    // Sync local properties with service (for backward compatibility)
    this.dailyRoute.set(null)
    this.projectedRoute.set(null)
    this.dailyRouteInfo = null
    this.projectedRouteInfo = null
    this.currentRouteDeparture = null
    this.currentRouteReturn = null
    this.projectedRouteReturn = null
    this.distancesToAffair.clear()
    this.distanceLines.set([])
  }

  /**
   * Detect HOTEL events from the daily events array and update location types/coordinates.
   * An HOTEL event on the current day means the technician ends the day at the hotel.
   * This is called after fetching daily events to ensure the map and panel reflect the hotel.
   */
  private detectHotelEventsFromDailyEvents(events: any[]): void {
    const hotelEvent = events.find(
      (e) =>
        (e.data?.eventType?.name === 'HOTEL' || e.eventTypeName === 'HOTEL') &&
        (e.data?.latitude || e.data?.workSiteLatitude || e.workSiteLatitude) &&
        (e.data?.longitude || e.data?.workSiteLongitude || e.workSiteLongitude)
    )
    if (hotelEvent) {
      const lat = parseFloat(
        hotelEvent.data?.latitude ||
          hotelEvent.data?.workSiteLatitude ||
          hotelEvent.workSiteLatitude
      )
      const lon = parseFloat(
        hotelEvent.data?.longitude ||
          hotelEvent.data?.workSiteLongitude ||
          hotelEvent.workSiteLongitude
      )
      if (!isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {
        this.endLocationType = 'hotel'
        this.endLocationCoords = { lat, lon }

        // Extract city info from hotel event
        const postalCode =
          hotelEvent.data?.postalCode || hotelEvent.postalCode || ''
        const locality =
          hotelEvent.data?.locality ||
          hotelEvent.data?.workSiteLocality ||
          hotelEvent.workSiteLocality ||
          hotelEvent.locality ||
          ''
        this.endLocationCity =
          postalCode && locality
            ? `${postalCode} ${locality}`
            : locality || postalCode || ''
      }
    }
  }

  /**
   * Calculate distances from the affair to each daily event
   * Delegates to RouteCalculationService
   * @param events The daily calendar events
   * @param cacheKey Optional cache key to store results
   */
  calculateDistancesToAffair(events: any[], cacheKey?: string): void {
    if (!this.affair || !this.hasValidCoordinates()) {
      this.distancesToAffair.clear()
      return
    }

    this.loadingDistancesToAffair = true

    this.routeCalculationService
      .calculateDistancesToAffair(
        {
          workSiteLatitude: this.affair.workSiteLatitude,
          workSiteLongitude: this.affair.workSiteLongitude,
        },
        events,
        cacheKey
      )
      .subscribe({
        next: () => {
          this.loadingDistancesToAffair = false
          // Sync local map with service
          this.distancesToAffair = new Map(
            this.routeCalculationService.distancesToAffair
          )
          // Update distance lines on map
          this.updateDistanceLines(events)
        },
        error: () => {
          this.loadingDistancesToAffair = false
          this.distancesToAffair.clear()
          this.distanceLines.set([])
        },
      })
  }

  /**
   * Calculate home departure and return time for the current daily route
   * Delegates to RouteCalculationService
   * @param events The daily calendar events
   * @param technician The technician for the day
   */
  calculateCurrentRouteDeparture(events: any[], technician: any): void {
    // Delegate to service and sync when API responds
    this.routeCalculationService
      .calculateCurrentRouteDeparture(
        events,
        technician,
        this.startLocationCoords,
        this.endLocationCoords
      )
      .subscribe({
        next: () => {
          this.currentRouteDeparture =
            this.routeCalculationService.currentRouteDeparture
          this.currentRouteReturn =
            this.routeCalculationService.currentRouteReturn
        },
      })
  }

  /**
   * Calculate projected return time when an affair is pre-selected
   * Delegates to RouteCalculationService
   * @param slot The pre-selected time slot
   * @param technician The technician
   */
  calculateProjectedReturnTime(slot: any, technician: any): void {
    if (!this.affair) {
      this.projectedRouteReturn = null
      return
    }

    // Delegate to service
    this.routeCalculationService
      .calculateProjectedReturnTime(
        slot,
        technician,
        {
          workSiteLatitude: this.affair.workSiteLatitude,
          workSiteLongitude: this.affair.workSiteLongitude,
        },
        this.interventionDuration,
        this.endLocationCoords
      )
      .subscribe({
        next: () => {
          this.projectedRouteReturn =
            this.routeCalculationService.projectedRouteReturn
        },
      })
  }

  /**
   * Update distance lines on the map from affair to each event
   * Delegates to RouteCalculationService
   */
  updateDistanceLines(events: any[]): void {
    if (!this.affair || !this.hasValidCoordinates()) {
      this.distanceLines.set([])
      return
    }

    // Delegate to service
    this.routeCalculationService.updateDistanceLines(
      {
        workSiteLatitude: this.affair.workSiteLatitude,
        workSiteLongitude: this.affair.workSiteLongitude,
      },
      events
    )

    // Sync local signal with service
    this.distanceLines.set(this.routeCalculationService.distanceLines())
  }

  /**
   * Update the slot indicators cache for the current intervention
   * and calculate for other interventions on the same day/technician
   */
  updateInterventionSlotIndicators(): void {
    if (!this.selectedIntervention) return

    // Don't overwrite indicators that were already calculated from batch
    // Only update if not already calculated or if we need to recalculate
    const existingIndicator = this.interventionSlotIndicators.get(
      this.selectedIntervention.id
    )
    if (existingIndicator?.calculated) {
      // Already calculated from batch - don't overwrite
      return
    }

    // Calculate and store indicators for selected intervention
    const feasibleCount = this.getFeasibleSlotsCount()
    const adjustableCount = this.getAdjustableSlotsCount()
    const shiftCount = this.getShiftSlotsCount()
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    this.interventionSlotIndicators.set(this.selectedIntervention.id, {
      feasibleCount,
      adjustableCount,
      shiftCount,
      totalSlots,
      calculated: true,
    })

    // Calculate indicators for other interventions of same day/tech if we have daily events cached
    this.calculateIndicatorsForSameDayInterventions()
  }

  /**
   * Calculate slot indicators for other interventions on the same day/technician
   * Uses cached daily events data
   */
  calculateIndicatorsForSameDayInterventions(): void {
    if (!this.selectedIntervention || !this.dailyCalendarEvents.length) return

    const selectedTech = this.selectedIntervention.usersTechnicians?.[0]
    if (!selectedTech) return

    const selectedDate = this.formatDate(
      new Date(this.selectedIntervention.startDate)
    )

    // Find other interventions in closestInterventions that match same day and technician
    this.closestInterventions.forEach((intervention: any) => {
      if (intervention.id === this.selectedIntervention.id) return
      if (this.interventionSlotIndicators.has(intervention.id)) return // Already calculated

      const interventionDate = this.formatDate(new Date(intervention.startDate))
      const interventionTech = intervention.usersTechnicians?.[0]

      // Check if same day and same technician
      if (interventionDate !== selectedDate) return
      if (!interventionTech || interventionTech.email !== selectedTech.email)
        return

      // This intervention shares the same day and tech - calculate its indicators
      // We need to fetch its availabilities to calculate
      this.calculateIndicatorsForIntervention(intervention)
    })
  }

  /**
   * Calculate slot indicators for a specific intervention using cached daily data
   */
  calculateIndicatorsForIntervention(intervention: any): void {
    if (!intervention.usersTechnicians?.length) return

    const technicianFromIntervention = intervention.usersTechnicians[0]
    const userToFetch = TechnicianTools.findByEmail(
      technicianFromIntervention.email,
      this.users
    )
    if (!userToFetch) return

    // Fetch availabilities for this intervention
    const interventionDate = new Date(intervention.startDate)
    const startOfDay = new Date(interventionDate)
    startOfDay.setHours(8, 0, 0, 0)
    const endOfDay = new Date(interventionDate)
    endOfDay.setHours(18, 0, 0, 0)

    const requestData = {
      start_date: this.formatDateTimeString(startOfDay, '08:00:00'),
      end_date: this.formatDateTimeString(endOfDay, '18:00:00'),
      duration: this.interventionDuration,
      user_ids: [userToFetch.id],
    }

    this.usersService.getAvailabilities(requestData).subscribe({
      next: (availabilities: any[]) => {
        if (!availabilities?.[0]?.availabilities?.available_slots) return

        const slots = availabilities[0].availabilities.available_slots
        const eventsWithCoords = AvailabilityTools.filterEventsWithCoordinates(
          this.dailyCalendarEvents
        )
        const sortedEvents =
          AvailabilityTools.sortEventsByStartTime(eventsWithCoords)

        if (sortedEvents.length === 0) {
          // No events with coords - all slots are feasible based on duration only
          let feasible = 0
          slots.forEach((slot: any) => {
            const duration = AvailabilityTools.getSlotDurationMinutes(slot)
            if (duration >= this.interventionDuration) feasible++
          })
          this.interventionSlotIndicators.set(intervention.id, {
            feasibleCount: feasible,
            adjustableCount: 0,
            shiftCount: 0,
            totalSlots: slots.length,
            calculated: true,
          })
          return
        }

        // Calculate feasible and adjustable slots using utilities
        let feasible = 0
        let adjustable = 0
        const minEffectiveTime = this.interventionDuration * 0.8

        slots.forEach((slot: any) => {
          const slotStart = new Date(slot.start)
          const slotEnd = new Date(slot.end)

          const prevResult = AvailabilityTools.findPreviousEvent(
            slotStart,
            sortedEvents
          )
          const nextResult = AvailabilityTools.findNextEvent(
            slotEnd,
            sortedEvents
          )

          const travelFromPrevious = prevResult
            ? (this.distancesToAffair.get(`event-${prevResult.index}`)
                ?.duration_min ?? null)
            : null
          const travelToNext = nextResult
            ? (this.distancesToAffair.get(`event-${nextResult.index}`)
                ?.duration_min ?? null)
            : null

          const info = AvailabilityTools.calculateSlotTravelConstraints({
            slotStart,
            slotEnd,
            previousEvent: prevResult?.event || null,
            nextEvent: nextResult?.event || null,
            travelFromPrevious,
            travelToNext,
            interventionDuration: this.interventionDuration,
          })

          // Skip slots where effective time is less than 80% of required duration
          if (
            info.effectiveAvailableTime !== null &&
            info.effectiveAvailableTime < minEffectiveTime
          ) {
            return // Don't count this slot
          }

          if (info.isFeasible) feasible++
          else if (info.needsAdjustment) adjustable++
        })

        this.interventionSlotIndicators.set(intervention.id, {
          feasibleCount: feasible,
          adjustableCount: adjustable,
          shiftCount: 0,
          totalSlots: slots.length,
          calculated: true,
        })
      },
      error: (err: any) => {
        console.error(
          'Error calculating indicators for intervention:',
          intervention.id,
          err
        )
      },
    })
  }

  /**
   * Check if a slot meets time-of-day constraints:
   * - Home departure must not be before 7:00 AM
   * - Home return must not be after 7:30 PM (19:30)
   */
  private slotMeetsTimeConstraints(slotIndex: number, slot: any): boolean {
    const travelInfo = this.slotTravelInfo.get(slotIndex)
    if (!travelInfo) return true

    // Check home departure time (for first slot of day)
    if (travelInfo.isFirstSlotOfDay && travelInfo.homeDepartureTime) {
      const departureHour = travelInfo.homeDepartureTime.getHours()
      // Before 7:00 AM - not allowed
      if (departureHour < 7) {
        return false
      }
    }

    // Check home return time using backend-calculated value
    if (travelInfo.isLastSlotOfDay && travelInfo.homeReturnTime) {
      const returnHour = travelInfo.homeReturnTime.getHours()
      const returnMin = travelInfo.homeReturnTime.getMinutes()
      // After 19:30 (7:30 PM) - not allowed
      if (returnHour > 19 || (returnHour === 19 && returnMin > 30)) {
        return false
      }
    }

    // Fallback: if we don't have homeReturnTime but it's the last slot, estimate it
    if (
      travelInfo.isLastSlotOfDay &&
      !travelInfo.homeReturnTime &&
      travelInfo.travelToHome
    ) {
      const slotStart = new Date(slot.start)
      const returnTime = new Date(
        slotStart.getTime() +
          (this.interventionDuration + travelInfo.travelToHome) * 60 * 1000
      )
      const returnHour = returnTime.getHours()
      const returnMin = returnTime.getMinutes()
      if (returnHour > 19 || (returnHour === 19 && returnMin > 30)) {
        return false
      }
    }

    return true
  }

  /**
   * Check if a slot meets the minimum 80% effective time threshold and time constraints
   */
  private slotMeetsAllConstraints(slotIndex: number): boolean {
    const slots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
    if (!slots || !slots[slotIndex]) return true

    const slot = slots[slotIndex]

    // Check time-of-day constraints (departure before 7:00, return after 19:30)
    if (!this.slotMeetsTimeConstraints(slotIndex, slot)) return false

    // Check 80% effective time threshold
    const travelInfo = this.slotTravelInfo.get(slotIndex)
    if (!travelInfo) return true

    // For slots that need adjustment (SERRÉ), check raw available time
    // because if we shift adjacent events, we can use the full slot
    if (travelInfo.needsAdjustment) {
      return travelInfo.availableTime >= this.interventionDuration
    }

    if (
      travelInfo.effectiveAvailableTime === null ||
      travelInfo.effectiveAvailableTime === undefined
    )
      return true
    const minEffectiveTime = this.interventionDuration * 0.8
    return travelInfo.effectiveAvailableTime >= minEffectiveTime
  }

  /**
   * Calculate constrained slot counts directly from batch result
   * Delegates to indicator service
   */
  calculateConstrainedSlotCounts(result: BatchInterventionResult): {
    feasibleCount: number
    adjustableCount: number
    shiftCount: number
    totalSlots: number
  } {
    return this.indicatorService.calculateConstrainedSlotCounts(result)
  }

  getFeasibleSlotsCount(): number {
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    if (this.slotTravelInfo.size === 0) return totalSlots

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      // Skip slots that don't meet all constraints
      if (!this.slotMeetsAllConstraints(i)) continue

      const info = this.slotTravelInfo.get(i)
      // Exclure les shift-slots - ils ont leur propre catégorie "décaler"
      if (info?.requiresShift) continue
      if (!info || info.isFeasible) count++
    }
    return count
  }

  getAdjustableSlotsCount(): number {
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    if (this.slotTravelInfo.size === 0) return 0

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      // Skip slots that don't meet all constraints
      if (!this.slotMeetsAllConstraints(i)) continue

      // Only count slots that can actually be adjusted (not blocked by do_not_shift)
      if (this.isSlotAdjustable(i)) count++
    }
    return count
  }

  /**
   * Check if a slot that needs adjustment is actually adjustable
   * A slot is NOT adjustable if it requires shifting an event that has do_not_shift = true
   */
  isSlotAdjustable(slotIndex: number): boolean {
    const info = this.slotTravelInfo.get(slotIndex)
    if (!info) return false
    if (!info.needsAdjustment) return false

    // If the slot needs adjustment, check if adjacent events can be shifted
    // If either adjacent event has do_not_shift = true, the slot is NOT adjustable
    if (info.previousEventDoNotShift === true) return false
    if (info.nextEventDoNotShift === true) return false

    return true
  }

  /**
   * Check if a slot is blocked (needs adjustment but cannot be adjusted)
   * because adjacent events have do_not_shift = true
   */
  isSlotBlocked(slotIndex: number): boolean {
    const info = this.slotTravelInfo.get(slotIndex)
    if (!info) return false
    if (!info.needsAdjustment) return false

    // The slot needs adjustment but at least one adjacent event cannot be shifted
    return (
      info.previousEventDoNotShift === true || info.nextEventDoNotShift === true
    )
  }

  /**
   * Get count of blocked slots (need adjustment but cannot be adjusted)
   */
  getBlockedSlotsCount(): number {
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    if (this.slotTravelInfo.size === 0) return 0

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      if (!this.slotMeetsAllConstraints(i)) continue
      if (this.isSlotBlocked(i)) count++
    }
    return count
  }

  /**
   * Check if a slot is a shift-slot (requires moving an existing unlocked intervention)
   */
  isShiftSlot(slotIndex: number): boolean {
    const travelInfo = this.slotTravelInfo.get(slotIndex)
    return travelInfo?.requiresShift === true
  }

  /**
   * Get count of shift-slots
   */
  getShiftSlotsCount(): number {
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    if (this.slotTravelInfo.size === 0) return 0

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      if (this.isShiftSlot(i)) count++
    }
    return count
  }

  /**
   * Generate cache key for technician/day combination
   */
  private getDailyCacheKey(technicianId: number, date: Date): string {
    const dateStr = this.formatDate(date)
    return `${technicianId}_${dateStr}`
  }

  /**
   * Fetch calendar events for the selected day and technician, then calculate the route
   * Uses cache to avoid recalculating when switching between interventions of the same technician/day
   * @param intervention The selected intervention
   */
  fetchDailyCalendarEventsAndRoute(intervention: any): void {
    if (
      !intervention ||
      !intervention.usersTechnicians ||
      intervention.usersTechnicians.length === 0
    ) {
      this.clearDailyRoute()
      return
    }

    // Get the first technician from the intervention
    const technicianFromIntervention = intervention.usersTechnicians[0]

    // Find the corresponding user in our users array by email to get the correct ID
    const userToFetch = this.users.find(
      (user: any) => user.email === technicianFromIntervention.email
    )

    if (!userToFetch) {
      console.warn(
        'Could not find user with email:',
        technicianFromIntervention.email
      )
      this.clearDailyRoute()
      return
    }

    // Get the date of the intervention
    const interventionDate = new Date(intervention.startDate)
    const dateFrom = this.formatDate(interventionDate)
    const dateTo = this.formatDate(interventionDate)

    if (!dateFrom || !dateTo) {
      this.clearDailyRoute()
      return
    }

    // Check cache for this technician/day combination
    const cacheKey = this.getDailyCacheKey(userToFetch.id, interventionDate)
    const cachedData = this.dailyDataCache.get(cacheKey)

    if (cachedData) {
      // Use cached data
      this.dailyCalendarEvents = cachedData.events
      this.dailyRouteInfo = cachedData.routeInfo
      this.distancesToAffair = new Map(cachedData.distancesToAffair)

      // Detect HOTEL events to update end location type/coords
      this.detectHotelEventsFromDailyEvents(cachedData.events)

      // Update markers (slot travel info is now calculated by the backend)
      this.updateDailyScheduleMarkers(cachedData.events, userToFetch)
      // Note: calculateSlotTravelInfo() is no longer needed - backend handles it via singleAvailabilityWithTravel

      // Recalculate routes even with cached data (routes are not cached, only metrics)
      if (cachedData.events && cachedData.events.length > 0) {
        this.calculateDailyRoute(userToFetch.id, cachedData.events, cacheKey)
      } else {
        this.loadingDailyRoute = false
        this.dailyRoute.set(null)
        this.dailyRouteInfo = null
        this.calculateProjectedRoute(userToFetch.id, [])
      }
      return
    }

    this.loadingDailyRoute = true

    // Fetch interventions AND events in parallel for this technician on this day
    const params = {
      users: [userToFetch.id],
      dateFrom: dateFrom,
      dateTo: dateTo,
    }
    forkJoin({
      interventions: this.interventionsService.getInterventions(params),
      events: this.eventsService.getEvents(params),
    }).subscribe({
      next: ({ interventions, events: calendarEvents }) => {
        // Merge interventions and events into a single array
        const allEvents: any[] = [...interventions, ...calendarEvents]
        this.dailyCalendarEvents = allEvents

        // Detect HOTEL events to update end location type/coords
        this.detectHotelEventsFromDailyEvents(allEvents)

        // Create schedule markers with times
        this.updateDailyScheduleMarkers(allEvents, userToFetch)

        // Calculate distances from affair to each event (for map display)
        // Note: Slot travel info is now calculated by the backend via singleAvailabilityWithTravel
        // Pass cacheKey to store results in cache
        this.calculateDistancesToAffair(allEvents, cacheKey)

        // Calculate home departure time for current route
        this.calculateCurrentRouteDeparture(allEvents, userToFetch)

        // If there are events, calculate the route
        if (allEvents && allEvents.length > 0) {
          this.calculateDailyRoute(userToFetch.id, allEvents, cacheKey)
        } else {
          this.loadingDailyRoute = false
          this.dailyRoute.set(null)
          this.dailyRouteInfo = null
          // Even with no existing events, calculate projected route for just the affair
          this.calculateProjectedRoute(userToFetch.id, [])
        }
      },
      error: (error) => {
        console.error('Error fetching daily calendar events:', error)
        this.loadingDailyRoute = false
        this.clearDailyRoute()
      },
    })
  }

  /**
   * Update markers for daily schedule events with time display
   * @param events Calendar events for the day
   * @param technician The technician for these events
   */
  updateDailyScheduleMarkers(events: any[], technician: any): void {
    if (!events || events.length === 0) {
      this.markersDailySchedule.set([])
      return
    }

    // Sort events by start time
    const sortedEvents = [...events].sort((a, b) => {
      const timeA = new Date(a.start || a.startDate).getTime()
      const timeB = new Date(b.start || b.startDate).getTime()
      return timeA - timeB
    })

    // Group events by location (to handle multiple interventions at same place)
    const tolerance = 0.0001 // ~10m tolerance for same location
    const locationGroups = new Map<
      string,
      { events: any[]; indices: number[] }
    >()

    let mapOrder = 0
    sortedEvents.forEach((event) => {
      // Skip HOTEL events — they are handled as start/end location markers
      if (
        event.data?.eventType?.name === 'HOTEL' ||
        event.eventTypeName === 'HOTEL'
      ) {
        return
      }

      const lat =
        event.data?.workSiteLatitude ||
        event.workSiteLatitude ||
        event.data?.latitude ||
        event.latitude
      const lng =
        event.data?.workSiteLongitude ||
        event.workSiteLongitude ||
        event.data?.longitude ||
        event.longitude

      if (!lat || !lng || +lat === 0 || +lng === 0) {
        return // Skip events without valid coordinates
      }

      mapOrder++

      // Find if there's already a group at this location
      let foundGroupKey: string | null = null
      for (const [key] of locationGroups) {
        const [groupLat, groupLng] = key.split(',').map(Number)
        if (
          Math.abs(groupLat - +lat) < tolerance &&
          Math.abs(groupLng - +lng) < tolerance
        ) {
          foundGroupKey = key
          break
        }
      }

      if (foundGroupKey) {
        // Add to existing group
        locationGroups.get(foundGroupKey)!.events.push(event)
        locationGroups.get(foundGroupKey)!.indices.push(mapOrder)
      } else {
        // Create new group
        locationGroups.set(`${lat},${lng}`, {
          events: [event],
          indices: [mapOrder],
        })
      }
    })

    // Create markers for each location group
    const scheduleMarkers: markerInputModel[] = []

    // Add home marker at start if technician has home coordinates
    const homeLatitude = technician?.homeLatitude || technician?.home_latitude
    const homeLongitude =
      technician?.homeLongitude || technician?.home_longitude
    const technicianName =
      technician?.firstname && technician?.lastname
        ? `${technician.firstname} ${technician.lastname}`
        : technician?.name || 'Technicien'

    // Determine start location: hotel if available, otherwise home
    const startLat = this.startLocationCoords?.lat ?? homeLatitude
    const startLon = this.startLocationCoords?.lon ?? homeLongitude
    const startEmoji = this.startLocationType === 'hotel' ? '🏨' : '🏠'
    const startLabel = this.startLocationType === 'hotel' ? 'Hôtel' : 'Départ'

    if (startLat && startLon && +startLat !== 0 && +startLon !== 0) {
      scheduleMarkers.push({
        coordinates: [+startLat, +startLon],
        tooltipText: `${startEmoji} ${startLabel} - ${technicianName}`,
        iconColor: technician?.color || '#ff6b35',
        markerType:
          this.startLocationType === 'hotel'
            ? ('hotel' as const)
            : ('home' as const),
        id: `daily-home-start-${technician?.id || 'tech'}`,
      })
    }

    locationGroups.forEach((group, positionKey) => {
      const [lat, lng] = positionKey.split(',').map(Number)
      const eventCount = group.events.length

      // Build time strings and orders for all events at this location
      const timeStrings = group.events.map((event) => {
        const startDate = new Date(event.start || event.startDate)
        return startDate.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
        })
      })

      // Build tooltip with all interventions/events
      const tooltipLines = group.events.map((event, i) => {
        const isEvent = event.data?.resource === 'event'
        const label = isEvent
          ? event.data?.eventType?.name ||
            event.title ||
            event.name ||
            'Événement'
          : event.title || event.name || 'Intervention'
        return `${group.indices[i]}. ${label} - ${timeStrings[i]}`
      })

      if (eventCount === 1) {
        // Single event - standard marker
        scheduleMarkers.push({
          coordinates: [lat, lng],
          tooltipText: tooltipLines[0],
          iconColor: technician?.color || '#ff6b35',
          markerType: 'schedule' as const,
          scheduleTime: timeStrings[0],
          scheduleOrder: group.indices[0],
          id: `schedule-${group.events[0].id || group.indices[0]}`,
        })
      } else {
        // Multiple events at same location - grouped marker
        const ordersText = group.indices.join('+')
        const timesText = timeStrings.join(' / ')

        scheduleMarkers.push({
          coordinates: [lat, lng],
          tooltipText: tooltipLines.join('\n'),
          iconColor: technician?.color || '#ff6b35',
          markerType: 'schedule' as const,
          scheduleTime: timesText,
          scheduleOrder: group.indices[0], // Show first order, rest in time
          scheduleGroupedOrders: ordersText, // New property for grouped display
          id: `schedule-group-${positionKey}`,
        })
      }
    })

    // Add end location marker (home or hotel)
    const endLat = this.endLocationCoords?.lat ?? homeLatitude
    const endLon = this.endLocationCoords?.lon ?? homeLongitude
    const endEmoji = this.endLocationType === 'hotel' ? '🏨' : '🏠'
    const endLabel = this.endLocationType === 'hotel' ? 'Hôtel' : 'Retour'

    if (endLat && endLon && +endLat !== 0 && +endLon !== 0) {
      scheduleMarkers.push({
        coordinates: [+endLat, +endLon],
        tooltipText: `${endEmoji} ${endLabel} - ${technicianName}`,
        iconColor: technician?.color || '#ff6b35',
        markerType:
          this.endLocationType === 'hotel'
            ? ('hotel' as const)
            : ('home' as const),
        id: `daily-home-end-${technician?.id || 'tech'}`,
      })
    }

    this.markersDailySchedule.set(scheduleMarkers)
  }

  /**
   * Update daily schedule markers for shift-slot mode.
   * Rebuilds markers with projected numbering: existing events (excluding shifted),
   * new affair inserted at the correct position, shifted event shown as last purple marker.
   */
  updateDailyScheduleMarkersForShift(
    events: any[],
    technician: any,
    shiftedEventId: number | null
  ): void {
    if (!this.preSelectedSlot?.slot?.start) {
      this.markersDailySchedule.set([])
      return
    }

    if (!events) {
      events = []
    }

    const selectedStartTime = new Date(
      this.preSelectedSlot.slot.start
    ).getTime()

    // Separate shifted event from the rest and sort
    const sortedEvents = [...events].sort((a, b) => {
      const timeA = new Date(a.start || a.startDate).getTime()
      const timeB = new Date(b.start || b.startDate).getTime()
      return timeA - timeB
    })

    const shiftedEvent = shiftedEventId
      ? sortedEvents.find((e) => (e.data?.id ?? e.id) === shiftedEventId)
      : null
    const filteredEvents = shiftedEventId
      ? sortedEvents.filter((e) => (e.data?.id ?? e.id) !== shiftedEventId)
      : sortedEvents

    // Build ordered list: filtered events + affair inserted chronologically
    const orderedItems: {
      event: any
      isAffair: boolean
      isShifted: boolean
    }[] = []
    let affairInserted = false

    for (const event of filteredEvents) {
      const eventTime = new Date(event.start || event.startDate).getTime()
      if (!affairInserted && selectedStartTime < eventTime) {
        orderedItems.push({
          event: {
            id: `affair-projected-${this.affair?.id || 'new'}`,
            title: this.affair?.name || 'Nouvelle intervention',
            start: this.preSelectedSlot.slot.start,
            data: {
              workSiteLatitude: this.affair?.workSiteLatitude,
              workSiteLongitude: this.affair?.workSiteLongitude,
            },
          },
          isAffair: true,
          isShifted: false,
        })
        affairInserted = true
      }
      orderedItems.push({ event, isAffair: false, isShifted: false })
    }

    if (!affairInserted) {
      orderedItems.push({
        event: {
          id: `affair-projected-${this.affair?.id || 'new'}`,
          title: this.affair?.name || 'Nouvelle intervention',
          start: this.preSelectedSlot.slot.start,
          data: {
            workSiteLatitude: this.affair?.workSiteLatitude,
            workSiteLongitude: this.affair?.workSiteLongitude,
          },
        },
        isAffair: true,
        isShifted: false,
      })
    }

    // Add shifted event as last numbered item
    if (shiftedEvent) {
      orderedItems.push({
        event: shiftedEvent,
        isAffair: false,
        isShifted: true,
      })
    }

    // Create markers with proper numbering
    const scheduleMarkers: markerInputModel[] = []
    const tolerance = 0.0001
    const homeLatitude = technician?.homeLatitude || technician?.home_latitude
    const homeLongitude =
      technician?.homeLongitude || technician?.home_longitude
    const technicianName =
      technician?.firstname && technician?.lastname
        ? `${technician.firstname} ${technician.lastname}`
        : technician?.name || 'Technicien'

    // Home start marker
    const startLat = this.startLocationCoords?.lat ?? homeLatitude
    const startLon = this.startLocationCoords?.lon ?? homeLongitude
    const startEmoji = this.startLocationType === 'hotel' ? '🏨' : '🏠'
    const startLabel = this.startLocationType === 'hotel' ? 'Hôtel' : 'Départ'

    if (startLat && startLon && +startLat !== 0 && +startLon !== 0) {
      scheduleMarkers.push({
        coordinates: [+startLat, +startLon],
        tooltipText: `${startEmoji} ${startLabel} - ${technicianName}`,
        iconColor: technician?.color || '#ff6b35',
        markerType:
          this.startLocationType === 'hotel'
            ? ('hotel' as const)
            : ('home' as const),
        id: `daily-home-start-${technician?.id || 'tech'}`,
      })
    }

    // Group by location with projected numbering
    const locationGroups = new Map<
      string,
      {
        items: { event: any; isAffair: boolean; isShifted: boolean }[]
        indices: number[]
      }
    >()

    let mapOrder = 0
    orderedItems.forEach((item) => {
      // Skip HOTEL events — they are handled as start/end location markers
      if (
        item.event.data?.eventType?.name === 'HOTEL' ||
        item.event.eventTypeName === 'HOTEL'
      ) {
        return
      }

      const lat =
        item.event.data?.workSiteLatitude ||
        item.event.workSiteLatitude ||
        item.event.data?.latitude ||
        item.event.latitude
      const lng =
        item.event.data?.workSiteLongitude ||
        item.event.workSiteLongitude ||
        item.event.data?.longitude ||
        item.event.longitude

      if (!lat || !lng || +lat === 0 || +lng === 0) {
        return
      }

      mapOrder++

      let foundGroupKey: string | null = null
      for (const [key] of locationGroups) {
        const [groupLat, groupLng] = key.split(',').map(Number)
        if (
          Math.abs(groupLat - +lat) < tolerance &&
          Math.abs(groupLng - +lng) < tolerance
        ) {
          foundGroupKey = key
          break
        }
      }

      if (foundGroupKey) {
        locationGroups.get(foundGroupKey)!.items.push(item)
        locationGroups.get(foundGroupKey)!.indices.push(mapOrder)
      } else {
        locationGroups.set(`${lat},${lng}`, {
          items: [item],
          indices: [mapOrder],
        })
      }
    })

    locationGroups.forEach((group, positionKey) => {
      const [lat, lng] = positionKey.split(',').map(Number)

      // Determine marker color: green for new affair, purple for shifted, tech color otherwise
      const hasAffair = group.items.some((i) => i.isAffair)
      const hasShifted = group.items.some((i) => i.isShifted)
      let markerColor = technician?.color || '#ff6b35'
      if (hasAffair) {
        markerColor = '#10b981' // Green
      } else if (hasShifted) {
        markerColor = '#9333ea' // Purple
      }

      const timeStrings = group.items.map((item) => {
        if (item.isShifted) return 'A reprog'
        const startDate = new Date(item.event.start || item.event.startDate)
        return startDate.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
        })
      })

      const tooltipLines = group.items.map((item, i) => {
        const suffix = item.isAffair
          ? ' ★ NOUVEAU'
          : item.isShifted
            ? ' ⚠ À DÉCALER'
            : ''
        const isEvent = item.event.data?.resource === 'event'
        const label = isEvent
          ? item.event.data?.eventType?.name ||
            item.event.title ||
            item.event.name ||
            'Événement'
          : item.event.title || item.event.name || 'Intervention'
        return `${group.indices[i]}. ${label} - ${timeStrings[i]}${suffix}`
      })

      if (group.items.length === 1) {
        scheduleMarkers.push({
          coordinates: [lat, lng],
          tooltipText: tooltipLines[0],
          iconColor: markerColor,
          markerType: 'schedule' as const,
          scheduleTime: timeStrings[0],
          scheduleOrder: group.indices[0],
          id: `schedule-proj-${group.items[0].event.id || group.indices[0]}`,
        })
      } else {
        const ordersText = group.indices.join('+')
        const timesText = timeStrings.join(' / ')
        scheduleMarkers.push({
          coordinates: [lat, lng],
          tooltipText: tooltipLines.join('\n'),
          iconColor: markerColor,
          markerType: 'schedule' as const,
          scheduleTime: timesText,
          scheduleOrder: group.indices[0],
          scheduleGroupedOrders: ordersText,
          id: `schedule-proj-group-${positionKey}`,
        })
      }
    })

    // End location marker (home or hotel)
    const endLat = this.endLocationCoords?.lat ?? homeLatitude
    const endLon = this.endLocationCoords?.lon ?? homeLongitude
    const endEmoji = this.endLocationType === 'hotel' ? '🏨' : '🏠'
    const endLabel = this.endLocationType === 'hotel' ? 'Hôtel' : 'Retour'

    if (endLat && endLon && +endLat !== 0 && +endLon !== 0) {
      scheduleMarkers.push({
        coordinates: [+endLat, +endLon],
        tooltipText: `${endEmoji} ${endLabel} - ${technicianName}`,
        iconColor: technician?.color || '#ff6b35',
        markerType:
          this.endLocationType === 'hotel'
            ? ('hotel' as const)
            : ('home' as const),
        id: `daily-home-end-${technician?.id || 'tech'}`,
      })
    }

    this.markersDailySchedule.set(scheduleMarkers)
  }

  /**
   * Calculate the route for the day's events
   * @param userId The technician's user ID
   * @param events The calendar events for the day
   * @param cacheKey Optional cache key to store results
   */
  calculateDailyRoute(userId: number, events: any[], cacheKey?: string): void {
    // Format interventions for the route API — exclude HOTEL events (handled as start/end location)
    const interventionsForRoute = events
      .filter(
        (event) =>
          event.data?.eventType?.name !== 'HOTEL' &&
          event.eventTypeName !== 'HOTEL'
      )
      .map((event, index) => {
        const lat =
          event.data?.workSiteLatitude ||
          event.workSiteLatitude ||
          event.data?.latitude ||
          event.latitude
        const lng =
          event.data?.workSiteLongitude ||
          event.workSiteLongitude ||
          event.data?.longitude ||
          event.longitude
        return {
          id: event.id || `event-${index}`,
          title: event.title || event.name || 'Intervention',
          start: event.start || event.startDate,
          data: {
            workSiteLatitude: lat,
            workSiteLongitude: lng,
          },
        }
      })
      .filter(
        (item) => item.data.workSiteLatitude && item.data.workSiteLongitude
      )

    // Prepare start/end location overrides for hotel
    const startLocation = this.startLocationCoords
      ? {
          latitude: this.startLocationCoords.lat,
          longitude: this.startLocationCoords.lon,
        }
      : null
    const endLocation = this.endLocationCoords
      ? {
          latitude: this.endLocationCoords.lat,
          longitude: this.endLocationCoords.lon,
        }
      : null

    this.interventionsService
      .getRoute(userId, interventionsForRoute, startLocation, endLocation)
      .subscribe({
        next: (routeResponse) => {
          this.loadingDailyRoute = false

          if (
            routeResponse?.geometry?.coordinates &&
            routeResponse.geometry.coordinates.length > 0
          ) {
            // Get technician color for the route
            const technician =
              events[0]?.usersTechnicians?.[0] ||
              this.users.find((u: any) => u.id === userId)
            const routeColor = technician?.color || '#ff6b35'

            this.dailyRoute.set({
              coordinates: routeResponse.geometry.coordinates,
              color: routeColor,
              weight: 4,
              opacity: 0.8,
            })

            // Store route summary info
            if (routeResponse.summary) {
              this.dailyRouteInfo = {
                distance_km: routeResponse.summary.distance_km,
                duration_min: routeResponse.summary.duration_min,
              }

              // Update cache with route info
              if (cacheKey) {
                const existingCache = this.dailyDataCache.get(cacheKey)
                if (existingCache) {
                  existingCache.routeInfo = this.dailyRouteInfo
                } else {
                  this.dailyDataCache.set(cacheKey, {
                    events: this.dailyCalendarEvents,
                    routeInfo: this.dailyRouteInfo,
                    distancesToAffair: new Map(this.distancesToAffair),
                  })
                }
              }
            }
          } else {
            this.dailyRoute.set(null)
          }
        },
        error: (error) => {
          console.error('Error calculating daily route:', error)
          this.loadingDailyRoute = false
          this.dailyRoute.set(null)
        },
      })

    // Calculate projected route (including the affair to schedule)
    this.calculateProjectedRoute(userId, events)
  }

  /**
   * Calculate the projected route including the affair to schedule
   * Only calculates if a slot has been pre-selected
   * @param userId The technician's user ID
   * @param existingEvents The current calendar events for the day
   */
  calculateProjectedRoute(userId: number, existingEvents: any[]): void {
    // Only calculate projected route if a slot has been pre-selected
    if (!this.preSelectedSlot?.slot?.start) {
      this.projectedRoute.set(null)
      this.projectedRouteInfo = null
      return
    }

    // Check if we have valid affair coordinates
    if (
      !this.affair?.workSiteLatitude ||
      !this.affair?.workSiteLongitude ||
      +this.affair.workSiteLatitude === 0 ||
      +this.affair.workSiteLongitude === 0
    ) {
      this.projectedRoute.set(null)
      this.projectedRouteInfo = null
      return
    }

    // If shift-slot selected, separate the shifted event (it will be added at end of route)
    let effectiveEvents = [...existingEvents]
    let shiftedEvent: any = null
    const isShiftSlot = this.preSelectedSlot?.travelInfo?.requiresShift === true
    const shiftedEventId =
      this.preSelectedSlot?.travelInfo?.shiftedEventId ?? null

    if (isShiftSlot && shiftedEventId) {
      shiftedEvent =
        effectiveEvents.find((e) => {
          const eventId = e.data?.id ?? e.id
          return eventId === shiftedEventId
        }) ?? null
      effectiveEvents = effectiveEvents.filter((e) => {
        const eventId = e.data?.id ?? e.id
        return eventId !== shiftedEventId
      })
    }

    // Get the start time from pre-selected slot
    const selectedStartTime = new Date(
      this.preSelectedSlot.slot.start
    ).getTime()
    const selectedStartDate = this.preSelectedSlot.slot.start

    // Create affair event to insert into the route at the correct position
    const affairEvent = {
      id: `affair-to-schedule-${this.affair.id || 'new'}`,
      title: this.affair.name || 'Nouvelle intervention',
      start: selectedStartDate,
      data: {
        workSiteLatitude: this.affair.workSiteLatitude,
        workSiteLongitude: this.affair.workSiteLongitude,
      },
      isAffairToSchedule: true, // Flag to identify this event
    }

    // Sort effective events by start time and insert affair at the correct chronological position
    const sortedEvents = [...effectiveEvents].sort((a, b) => {
      const timeA = new Date(a.start || a.startDate).getTime()
      const timeB = new Date(b.start || b.startDate).getTime()
      return timeA - timeB
    })

    // Find the correct position to insert the affair based on selected slot time
    let insertIndex = sortedEvents.length // Default: add at end
    for (let i = 0; i < sortedEvents.length; i++) {
      const eventTime = new Date(
        sortedEvents[i].start || sortedEvents[i].startDate
      ).getTime()
      if (selectedStartTime < eventTime) {
        insertIndex = i
        break
      }
    }

    // Insert affair at the correct position
    const allEvents = [
      ...sortedEvents.slice(0, insertIndex),
      affairEvent,
      ...sortedEvents.slice(insertIndex),
    ]

    // If shift-slot, add the shifted event at the end of the route
    // (it will be done later in the day, before returning home)
    if (shiftedEvent) {
      allEvents.push(shiftedEvent)
    }

    // Format interventions for the route API — exclude HOTEL events (handled as start/end location)
    const interventionsForRoute = allEvents
      .filter(
        (event) =>
          event.data?.eventType?.name !== 'HOTEL' &&
          event.eventTypeName !== 'HOTEL'
      )
      .map((event, index) => {
        const lat =
          event.data?.workSiteLatitude ||
          event.workSiteLatitude ||
          event.data?.latitude ||
          event.latitude
        const lng =
          event.data?.workSiteLongitude ||
          event.workSiteLongitude ||
          event.data?.longitude ||
          event.longitude
        return {
          id: event.id || `event-${index}`,
          title: event.title || event.name || 'Intervention',
          start: event.start || event.startDate,
          data: {
            workSiteLatitude: lat,
            workSiteLongitude: lng,
          },
        }
      })
      .filter(
        (item) => item.data.workSiteLatitude && item.data.workSiteLongitude
      )

    // Prepare start/end location overrides for hotel (projected route)
    const startLocation = this.startLocationCoords
      ? {
          latitude: this.startLocationCoords.lat,
          longitude: this.startLocationCoords.lon,
        }
      : null
    const endLocation = this.endLocationCoords
      ? {
          latitude: this.endLocationCoords.lat,
          longitude: this.endLocationCoords.lon,
        }
      : null

    this.interventionsService
      .getRoute(userId, interventionsForRoute, startLocation, endLocation)
      .subscribe({
        next: (routeResponse) => {
          if (
            routeResponse?.geometry?.coordinates &&
            routeResponse.geometry.coordinates.length > 0
          ) {
            // Use green dashed line for projected route
            this.projectedRoute.set({
              coordinates: routeResponse.geometry.coordinates,
              color: '#10B981', // Green color
              weight: 4,
              opacity: 0.8,
              dashArray: '10, 10', // Dashed line
            })

            // Store projected route summary info
            if (routeResponse.summary) {
              this.projectedRouteInfo = {
                distance_km: routeResponse.summary.distance_km,
                duration_min: routeResponse.summary.duration_min,
              }
            }
          } else {
            this.projectedRoute.set(null)
            this.projectedRouteInfo = null
          }
        },
        error: (error) => {
          console.error('Error calculating projected route:', error)
          this.projectedRoute.set(null)
          this.projectedRouteInfo = null
        },
      })
  }

  getSortedDailyEvents(): any[] {
    if (!this.dailyCalendarEvents?.length) return []
    return AvailabilityTools.sortEventsByStartTime(this.dailyCalendarEvents)
  }

  /**
   * Check if the affair should be inserted before the current event in the schedule list
   * @param event The current event being rendered
   * @param isFirstEvent Whether this is the first event in the list
   * @returns true if the affair should be inserted before this event
   */
  shouldInsertAffairBefore(event: any, isFirstEvent: boolean): boolean {
    if (!this.preSelectedSlot?.slot?.start) {
      return false
    }

    const slotTime = new Date(this.preSelectedSlot.slot.start).getTime()
    const eventTime = new Date(event.start || event.startDate).getTime()

    // For the first event, check if slot comes before it
    if (isFirstEvent) {
      return slotTime < eventTime
    }

    // For other events, check if this is the first event that comes after the slot
    const sortedEvents = this.getSortedDailyEvents()
    const currentIndex = sortedEvents.findIndex(
      (e) => (e.id || e.start) === (event.id || event.start)
    )

    if (currentIndex <= 0) {
      return false
    }

    const previousEvent = sortedEvents[currentIndex - 1]
    const previousEventTime = new Date(
      previousEvent.start || previousEvent.startDate
    ).getTime()

    // Insert before this event if slot is between previous and current event
    return slotTime >= previousEventTime && slotTime < eventTime
  }

  /**
   * Check if the affair should be inserted at the end of the schedule list
   * @returns true if the affair should be inserted after all existing events
   */
  shouldInsertAffairAtEnd(): boolean {
    if (!this.preSelectedSlot?.slot?.start) {
      return false
    }

    const sortedEvents = this.getSortedDailyEvents()

    // If no events, insert at end (which is also the beginning)
    if (sortedEvents.length === 0) {
      return true
    }

    const slotTime = new Date(this.preSelectedSlot.slot.start).getTime()
    const lastEvent = sortedEvents[sortedEvents.length - 1]
    const lastEventTime = new Date(
      lastEvent.start || lastEvent.startDate
    ).getTime()

    // Insert at end if slot comes after all events
    return slotTime >= lastEventTime
  }

  formatPreSelectedSlotTime(): string {
    if (!this.preSelectedSlot?.slot) return ''
    return DateTools.formatSlotTime(this.preSelectedSlot.slot)
  }

  /**
   * Format the home departure time from the pre-selected slot's travel info
   * @returns Formatted departure time string (e.g., "07:30")
   */
  formatHomeDepartureTime(): string {
    const travelInfo = this.preSelectedSlot?.travelInfo
    if (!travelInfo?.homeDepartureTime) return ''

    if (travelInfo.homeDepartureTime instanceof Date) {
      return travelInfo.homeDepartureTime.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const date = new Date(travelInfo.homeDepartureTime)
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Format the current route departure time from home
   * @returns Formatted departure time string (e.g., "07:30")
   */
  formatCurrentRouteDepartureTime(): string {
    if (!this.currentRouteDeparture?.departureTime) return ''

    return this.currentRouteDeparture.departureTime.toLocaleTimeString(
      'fr-FR',
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    )
  }

  /**
   * Format the first event time for current route
   * @returns Formatted first event time string (e.g., "08:00")
   */
  formatCurrentRouteFirstEventTime(): string {
    if (!this.currentRouteDeparture?.firstEventTime) return ''

    return this.currentRouteDeparture.firstEventTime.toLocaleTimeString(
      'fr-FR',
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    )
  }

  /**
   * Format the last event end time for current route return
   * @returns Formatted last event end time string (e.g., "17:00")
   */
  formatCurrentRouteLastEventEndTime(): string {
    if (!this.currentRouteReturn?.lastEventEndTime) return ''

    return this.currentRouteReturn.lastEventEndTime.toLocaleTimeString(
      'fr-FR',
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    )
  }

  /**
   * Format the return time to home for current route
   * @returns Formatted return time string (e.g., "18:00")
   */
  formatCurrentRouteReturnTime(): string {
    if (!this.currentRouteReturn?.returnTime) return ''

    return this.currentRouteReturn.returnTime.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Format the projected return time to home (when affair is pre-selected)
   * @returns Formatted projected return time string (e.g., "19:30")
   */
  formatProjectedReturnTime(): string {
    if (!this.projectedRouteReturn?.returnTime) return ''

    return this.projectedRouteReturn.returnTime.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Format the affair end time for projected return
   * @returns Formatted affair end time string (e.g., "18:30")
   */
  formatProjectedAffairEndTime(): string {
    if (!this.projectedRouteReturn?.affairEndTime) return ''

    return this.projectedRouteReturn.affairEndTime.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  formatEventTime(event: any): string {
    return DateTools.formatEventTime(event)
  }

  formatDuration(minutes: number): string {
    return DateTools.formatDuration(minutes)
  }

  /**
   * Generate Google Maps URL for the current daily route (without the affair)
   */
  getGoogleMapsUrlCurrentRoute(): string | null {
    if (!this.dailyCalendarEvents?.length) return null

    const technician = this.getSelectedTechnician()
    const techPostalCode = technician?.postalCode || ''
    const techLocality = technician?.locality || ''
    const hasHome = techPostalCode && techLocality

    // Sort events by start time
    const sortedEvents = [...this.dailyCalendarEvents].sort((a, b) => {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    })

    // Build waypoints from events using CP + Ville
    const waypoints: string[] = []

    // Home as origin
    let origin = ''
    if (hasHome) {
      origin = encodeURIComponent(`${techPostalCode} ${techLocality}, France`)
    } else if (sortedEvents[0]) {
      const firstPostalCode =
        sortedEvents[0].data?.workSitePostalCode ||
        sortedEvents[0].workSitePostalCode ||
        ''
      const firstLocality =
        sortedEvents[0].data?.workSiteLocality ||
        sortedEvents[0].workSiteLocality ||
        sortedEvents[0].data?.workSiteCity ||
        sortedEvents[0].workSiteCity ||
        ''
      if (firstPostalCode && firstLocality) {
        origin = encodeURIComponent(
          `${firstPostalCode} ${firstLocality}, France`
        )
      }
    }

    // Add all events as waypoints (CP + Ville)
    sortedEvents.forEach((event) => {
      const postalCode =
        event.data?.workSitePostalCode || event.workSitePostalCode || ''
      const locality =
        event.data?.workSiteLocality ||
        event.workSiteLocality ||
        event.data?.workSiteCity ||
        event.workSiteCity ||
        ''
      if (postalCode && locality) {
        waypoints.push(encodeURIComponent(`${postalCode} ${locality}, France`))
      }
    })

    // Add home as final destination (return home)
    if (hasHome) {
      waypoints.push(
        encodeURIComponent(`${techPostalCode} ${techLocality}, France`)
      )
    }

    // Remove consecutive duplicates (keep order but skip if same as previous)
    const uniqueWaypoints = waypoints.filter(
      (wp, index) => index === 0 || wp !== waypoints[index - 1]
    )

    if (!origin || uniqueWaypoints.length === 0) return null

    // Last waypoint is the destination (home)
    const destination = uniqueWaypoints.pop()
    const waypointsParam =
      uniqueWaypoints.length > 0
        ? `&waypoints=${uniqueWaypoints.join('|')}`
        : ''

    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypointsParam}&travelmode=driving`
  }

  /**
   * Generate Google Maps URL for the projected route (with the affair)
   */
  getGoogleMapsUrlWithAffair(): string | null {
    if (!this.affair) return null

    const technician = this.getSelectedTechnician()
    const techPostalCode = technician?.postalCode || ''
    const techLocality = technician?.locality || ''
    const hasHome = techPostalCode && techLocality

    // Get affair address
    const affairAddress =
      this.affair?.workSitePostalCode && this.affair?.workSiteLocality
        ? encodeURIComponent(
            `${this.affair.workSitePostalCode} ${this.affair.workSiteLocality}, France`
          )
        : null

    // Get the time when the affair should be inserted
    const affairStartTime = this.preSelectedSlot?.slot?.start
      ? new Date(this.preSelectedSlot.slot.start).getTime()
      : null

    // Sort events by start time
    const sortedEvents = this.dailyCalendarEvents?.length
      ? [...this.dailyCalendarEvents].sort((a, b) => {
          return (
            new Date(a.start || a.startDate).getTime() -
            new Date(b.start || b.startDate).getTime()
          )
        })
      : []

    // Build waypoints from events + affair using CP + Ville
    const waypoints: string[] = []

    // Home as origin
    let origin = ''
    if (hasHome) {
      origin = encodeURIComponent(`${techPostalCode} ${techLocality}, France`)
    } else if (affairAddress) {
      origin = affairAddress
    }

    // Track if affair has been inserted
    let affairInserted = false

    // Add all existing events as waypoints, inserting affair at the right position
    sortedEvents.forEach((event, index) => {
      const eventStartTime = new Date(event.start || event.startDate).getTime()

      // Insert affair before this event if affair should come first
      if (
        !affairInserted &&
        affairAddress &&
        affairStartTime &&
        affairStartTime < eventStartTime
      ) {
        waypoints.push(affairAddress)
        affairInserted = true
      }

      const postalCode =
        event.data?.workSitePostalCode || event.workSitePostalCode || ''
      const locality =
        event.data?.workSiteLocality ||
        event.workSiteLocality ||
        event.data?.workSiteCity ||
        event.workSiteCity ||
        ''
      if (postalCode && locality) {
        waypoints.push(encodeURIComponent(`${postalCode} ${locality}, France`))
      }
    })

    // If affair wasn't inserted yet (it comes after all events), add it now
    if (!affairInserted && affairAddress) {
      waypoints.push(affairAddress)
    }

    // Add home as final destination (return home)
    if (hasHome) {
      waypoints.push(
        encodeURIComponent(`${techPostalCode} ${techLocality}, France`)
      )
    }

    // Remove consecutive duplicates (keep order but skip if same as previous)
    const uniqueWaypoints = waypoints.filter(
      (wp, index) => index === 0 || wp !== waypoints[index - 1]
    )

    if (!origin || uniqueWaypoints.length === 0) return null

    // Last waypoint is the destination (home)
    const destination = uniqueWaypoints.pop()
    const waypointsParam =
      uniqueWaypoints.length > 0
        ? `&waypoints=${uniqueWaypoints.join('|')}`
        : ''

    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypointsParam}&travelmode=driving`
  }

  getSelectedTechnicianColor(): string {
    if (!this.selectedIntervention?.usersTechnicians?.[0]) return '#ff6b35'
    const tech = this.selectedIntervention.usersTechnicians[0]
    const fullUser = TechnicianTools.findByEmail(tech.email, this.users)
    return TechnicianTools.getColor(fullUser || tech, '#ff6b35')
  }

  getSelectedTechnician(): any {
    // Case 1: Direct technician selection (fallback mode)
    if (this.selectedTechnicianDirect) {
      return (
        TechnicianTools.findByEmail(
          this.selectedTechnicianDirect.email,
          this.users
        ) || this.selectedTechnicianDirect
      )
    }
    // Case 2: Technician from selected intervention
    if (!this.selectedIntervention?.usersTechnicians?.[0]) return null
    const tech = this.selectedIntervention.usersTechnicians[0]
    return TechnicianTools.findByEmail(tech.email, this.users) || tech
  }

  getSelectedTechnicianHomeCity(): string {
    return TechnicianTools.getHomeCity(this.getSelectedTechnician())
  }

  hasSelectedTechnicianHomeCoordinates(): boolean {
    return TechnicianTools.hasValidHomeCoordinates(this.getSelectedTechnician())
  }

  /**
   * Get affair address with postal code (format: "CP Ville")
   */
  getAffairCityWithPostalCode(): string {
    if (!this.affair) return 'Ville non renseignée'
    const postalCode = this.affair.workSitePostalCode || ''
    const city = this.affair.workSiteLocality || ''
    if (postalCode && city) {
      return `${postalCode} ${city}`
    }
    return city || postalCode || 'Ville non renseignée'
  }

  /**
   * Get the display name for the new item in daily info panel.
   * Uses newItemNameOverride if provided (e.g. mlRef for reschedule), otherwise defaults to finalCustomerLastname.
   */
  getNewItemName(): string {
    if (this.newItemNameOverride !== null) {
      return this.newItemNameOverride
    }
    return this.affair?.finalCustomerLastname || ''
  }

  // Add a method to add a technician to selection (simple - just updates form)
  addTechnicianToSelection(technician: UserModel) {
    // Single select: replace the current selection
    this.usersForm.get('users')?.setValue([technician])
    this.selectedTechnicianControl.setValue(technician)
    this.selectedTechnicians = [technician]
    this.emitTechnicians()
  }

  /**
   * Handle direct selection of a technician from "Domiciles proches" panel.
   * - If technician has a nearby intervention, select it
   * - Otherwise, show empty days and clear route
   */
  onHomeTechnicianDirectSelect(technician: UserModel) {
    // Update form selection
    this.addTechnicianToSelection(technician)

    // Check if technician has any nearby intervention in closestInterventions
    const technicianIntervention = this.closestInterventions?.find(
      (intervention: any) =>
        intervention.usersTechnicians?.some(
          (tech: any) => tech.email === technician.email
        )
    )

    if (technicianIntervention) {
      // Technician has a nearby intervention - select it
      this.onSelectIntervention(technicianIntervention)
    } else {
      // No nearby intervention - show empty days and clear route
      this.clearDailyRoute()
      this.clearPreSelectedSlot()
      this.selectedIntervention = null
      this.selectedInterventionChanged.emit(null)
      this.preferredAvailabilityTab = 'emptyDays'

      // Fetch availabilities and empty days for this technician
      this.fetchAvailabilitiesForTechnician(technician)
    }
  }

  // Handler for single select change
  onTechnicianSelected(technician: UserModel | null) {
    if (technician) {
      this.usersForm.get('users')?.setValue([technician])
      this.emitTechnicians()
    } else {
      this.usersForm.get('users')?.setValue([])
      this.emitTechnicians()
    }
  }

  getTechnicianInitials(technician: any): string {
    return TechnicianTools.getInitials(technician)
  }

  // Add this method to update the available technicians list
  updateAvailableTechnicians() {
    // Ensure this.users is at least an empty array
    if (!this.users) {
      this.users = []
    }

    const selectedIds = new Set(
      (this.usersForm.value.users || []).map((user: any) => user.id)
    )

    this.availableTechnicians = this.users.filter(
      (user: any) => !selectedIds.has(user.id)
    )
  }

  prepareUserData() {
    TechnicianTools.prepareUserData(this.users)
  }

  // Add method to reset the map
  resetMapComponent(): void {
    // Reset internal state
    this.hoveredInterventionId = null
    this.closestInterventions = []
    this.closestTechnicians = []

    // Reset markers
    this.updateAffairMarker()
    this.markersClosest.set([])

    // Reset date range to next week
    const nextWeekRange = this.getNextWeekRange()
    this.rangeDates.setValue(nextWeekRange)

    // Fetch updated data
    this.fetchClosestInterventions(nextWeekRange)
    if (this.affair?.workSiteDepartment) {
      this.fetchClosestTechnicians(this.affair.workSiteDepartment)
    }

    // Force map recreation on next tick
    setTimeout(() => {
      if (this.mapComponent) {
        // Force map to rebuild its bounds based on updated markers
        this.mapComponent.updateMarkerLayers()
        this.mapComponent.fitBounds()
      }
    }, 100)
  }

  // Add helper method to update affair marker
  updateAffairMarker(): void {
    if (this.affair) {
      this.markersAffair.set([
        {
          coordinates: [
            this.affair.workSiteLatitude,
            this.affair.workSiteLongitude,
          ],
          iconColor: 'red',
          iconHtmlClass: 'pi pi-home',
          tooltipText: `À programmer: ${this.affair.name || 'Affaire'}`,
          markerType: 'standard',
        },
      ])
    } else {
      this.markersAffair.set([])
    }
  }

  /**
   * Force refresh the map when the component becomes visible again
   * This fixes the issue where the map doesn't fill its container after navigation
   */
  private refreshMapWhenVisible(): void {
    // Use a sequence of timeouts to ensure DOM is ready
    setTimeout(() => {
      if (this.mapComponent) {
        this.mapComponent.invalidateSize()
      }
    }, 100)

    // Try again after a longer delay to be sure
    setTimeout(() => {
      if (this.mapComponent) {
        this.mapComponent.invalidateSize()
      }
    }, 500)
  }

  // Method to update the limit for displayed interventions
  updateInterventionsLimit(value: number): void {
    // Ensure the value is within valid limits
    this.interventionsLimit = Math.min(Math.max(1, value), 15)

    // Re-fetch interventions with the new limit
    if (this.rangeDates.value) {
      this.fetchClosestInterventions(this.rangeDates.value)
    }
  }

  // Method to update the sort type for interventions
  updateInterventionsSortBy(sortBy: 'distance' | 'date'): void {
    this.interventionsSortBy = sortBy

    // Re-fetch interventions with the new sort order
    if (this.rangeDates.value) {
      this.fetchClosestInterventions(this.rangeDates.value)
    }
  }

  /**
   * Pre-select a time slot from an empty day (journée creuse).
   * Clears the current daily route context and recalculates a route from home → affair → home.
   * @param availableSlot The selected available time slot
   * @param userEmail The user email of the technician
   * @param date The date string 'YYYY-MM-DD' of the empty day
   */
  preSelectEmptyDayTimeSlot(
    availableSlot: any,
    userEmail: string,
    date: string
  ): void {
    // Find the technician by email
    let technician = this.closestTechnicians.find(
      (tech) => tech.email === userEmail
    )
    if (!technician) {
      technician = this.users.find((user: any) => user.email === userEmail)
    }

    if (!technician) {
      return
    }

    // Clear the daily route context from any previously selected intervention day
    this.clearDailyRoute()

    // Store the pre-selected slot with empty day context
    this.preSelectedSlot = {
      slot: availableSlot,
      technician: technician,
      interventionDate: date,
      duration: this.interventionDuration,
      preSelectedAt: new Date().toISOString(),
      slotIndex: -1,
      travelInfo: null,
      isEmptyDay: true,
    }
    this.preSelectedSlotChanged.emit(this.preSelectedSlot) // Emit for persistence

    // Add technician to selection if not already selected
    this.addTechnicianToSelection(technician)

    // Calculate projected route: home → affair → home (no existing events)
    this.calculateProjectedRoute(technician.id, [])

    // Calculate projected return time
    this.calculateProjectedReturnTime(availableSlot, technician)

    // Update markers: show home + affair insertion marker
    this.updateDailyScheduleMarkersForShift([], technician, null)

    // Emit to parent component
    this.slotPreSelected.emit(this.preSelectedSlot)
  }

  /**
   * Pre-select a time slot for the next step (calendar step)
   * @param availableSlot The selected available time slot
   * @param userEmail The user email of the technician
   */
  preSelectTimeSlot(availableSlot: any, userEmail: string): void {
    // Find the technician by email
    let technician = this.closestTechnicians.find(
      (tech) => tech.email === userEmail
    )

    // If not found, try to find in users array
    if (!technician) {
      technician = this.users.find((user: any) => user.email === userEmail)
    }

    if (technician) {
      // Find the original index of this slot to get its travel info
      const availableSlots =
        this.selectedInterventionAvailabilities?.availabilities?.available_slots
      let slotIndex = -1
      let travelInfo = null

      if (availableSlots) {
        slotIndex = availableSlots.findIndex(
          (s: any) =>
            s.start === availableSlot.start && s.end === availableSlot.end
        )
        if (slotIndex >= 0) {
          travelInfo = this.slotTravelInfo.get(slotIndex)
        }
      }

      // Store the pre-selected slot with additional context
      this.preSelectedSlot = {
        slot: availableSlot,
        technician: technician,
        interventionDate: this.selectedIntervention?.startDate,
        duration: this.interventionDuration,
        preSelectedAt: new Date().toISOString(),
        slotIndex: slotIndex,
        travelInfo: travelInfo,
      }
      this.preSelectedSlotChanged.emit(this.preSelectedSlot) // Emit for persistence

      // Add technician to selection if not already selected
      this.addTechnicianToSelection(technician)

      // Recalculate projected route with the new pre-selected slot time
      if (this.dailyCalendarEvents && technician.id) {
        this.calculateProjectedRoute(technician.id, this.dailyCalendarEvents)
      }

      // Update map markers to reflect projected schedule (new affair + shifted event)
      if (this.dailyCalendarEvents && technician) {
        this.updateDailyScheduleMarkersForShift(
          this.dailyCalendarEvents,
          technician,
          travelInfo?.requiresShift ? travelInfo.shiftedEventId : null
        )
      }

      // Calculate projected return time if affair is at end of day
      this.calculateProjectedReturnTime(availableSlot, technician)

      // Emit to parent component
      this.slotPreSelected.emit(this.preSelectedSlot)

      // Show user feedback
      this.showPreSelectionFeedback(availableSlot, technician)
    }
  }

  /**
   * Show visual feedback when a slot is pre-selected
   */
  private showPreSelectionFeedback(slot: any, technician: any): void {
    // Visual feedback is handled by the template (highlighted slot)
  }

  /**
   * Get the pre-selected slot information for display
   * @returns Formatted string of pre-selected slot info
   */
  getPreSelectedSlotInfo(): string {
    if (!this.preSelectedSlot) {
      return ''
    }

    const slot = this.preSelectedSlot.slot
    const tech = this.preSelectedSlot.technician
    const startTime = new Date(slot.start).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
    const endTime = new Date(slot.end).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })

    return `${startTime}-${endTime} avec ${tech.firstname} ${tech.lastname}`
  }

  /**
   * Clear the pre-selected slot and restore the initial daily route
   */
  clearPreSelectedSlot(): void {
    const previousSlot = this.preSelectedSlot
    this.preSelectedSlot = null
    this.projectedRouteReturn = null // Clear projected return when slot is cleared
    this.projectedRoute.set(null)
    this.projectedRouteInfo = null
    this.slotPreSelected.emit(null)

    // Restore original daily schedule markers and recalculate initial route
    if (previousSlot?.technician) {
      const technician = previousSlot.technician

      if (previousSlot.isEmptyDay) {
        // Empty day: no existing events, clear everything
        this.clearDailyRoute()
      } else if (this.dailyCalendarEvents?.length > 0) {
        // Normal case: restore original markers (without projected affair)
        this.updateDailyScheduleMarkers(this.dailyCalendarEvents, technician)
        // Recalculate the original daily route (without the affair)
        this.calculateDailyRoute(technician.id, this.dailyCalendarEvents)
      } else {
        this.markersDailySchedule.set([])
        this.dailyRoute.set(null)
        this.dailyRouteInfo = null
      }
    }
  }
}
